import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Divider,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    useQCockpitMonitor,
    useQCockpitV2Monitor,
    useQCockpitV3Monitor,
    useQCockpitV4Monitor,
    useChanakyaMonitor,
    useChanakyaInverseMonitor,
    useViduraMonitor,
} from './useQCockpitMonitor';
import { useVyasaMonitor } from '../vyasa/useVyasaMonitor';
import { useGarudaMonitor } from '../garuda/useGarudaMonitor';
import { useViduraState } from './hooks/useViduraDecisions';
import { usePlatformStateModel } from './usePlatformStateModel';
import {
    EXECUTION_MODE_LIVE,
    EXECUTION_MODE_SIMULATION,
    filterFamiliesByMode,
    resolveFamilyForMode,
    useQCockpitFamilies,
} from './hooks/useQCockpitFamilies';
import TradeArchivePanel from './components/TradeArchivePanel';
import ViduraGatePanel from './components/ViduraGatePanel';
import DhruvaDesk from './dhruva/DhruvaDesk';
import KuberaDesk from './kubera/KuberaDesk';
import StrategySnapshotPanel from './components/StrategySnapshotPanel';
import StrategyEventFeedPanel from './components/StrategyEventFeedPanel';
import {
    buildEventFeedRows,
    buildChanakyaInverseStateMessage,
    buildViduraStateMessage,
    resolveChanakyaInverseLivePnl,
} from './strategyEventFeed';
import {
    PRIMARY_FONT,
    MONO_FONT,
    SURFACE_RADIUS,
    INNER_RADIUS,
    DensityContext,
    useDensity,
    formatCurrency,
    formatNumber,
    formatRelativeTime,
    formatTimestamp,
    formatChipText,
    toneForStatus,
    accentColorFor,
    getValueStyles,
    getSurfaceStyles,
    eyebrowSx,
    StatusBadge,
    MetricLabel,
    MetricMatrix,
    ToneBanner,
    SectionFrame,
    PanelFrame,
    DataTable,
} from './components/SharedFrames';

const HERO_RADIUS = '20px';
const COMFORTABLE_DENSITY = 'comfortable';
const COMPACT_DENSITY = 'compact';
const DENSITY_STORAGE_KEY = 'qcockpit:density';
const MODE_SIMULATION = 'simulation';
const MODE_LIVE = 'live';
const MODE_STORAGE_KEY = 'qcockpit:mode';
const FAMILY_STORAGE_KEY = 'qcockpit:strategyFamily';
const STRATEGY_FAMILIES = [
    { key: 'dhruva', label: 'Dhruva' },
    { key: 'cobra', label: 'Cobra' },
    { key: 'kubera', label: 'Kubera' },
    { key: 'vyasa', label: 'Vyasa' },
    { key: 'garuda', label: 'Garuda' },
    { key: 'vidura', label: 'Vidura' },
    { key: 'chanakya', label: 'Chanakya' },
    { key: 'chanakya_inverse', label: 'Chanakya Inverse' },
    { key: 'arjuna', label: 'Arjuna' },
    { key: 'narasimha', label: 'Narasimha' },
];

const CHANAKYA_DESK_FAMILIES = new Set(['chanakya', 'chanakya_inverse']);
const IMPLEMENTED_DESK_FAMILIES = new Set(['dhruva', 'kubera', 'cobra', 'chanakya', 'chanakya_inverse', 'vyasa', 'garuda', 'vidura']);
// Families with their own desk component: the shared cobra-shaped desk is skipped for them.
const OWN_DESK_FAMILIES = new Set(['dhruva', 'kubera']);
const isChanakyaDesk = (family) => CHANAKYA_DESK_FAMILIES.has(family);
const isImplementedDeskFamily = (family) => IMPLEMENTED_DESK_FAMILIES.has(family);
const chanakyaDeskTitle = (family) => (
    family === 'chanakya_inverse' ? 'Chanakya Inverse' : 'Chanakya'
);
const chanakyaDeskSubtitle = (family, executionMode = EXECUTION_MODE_SIMULATION) => {
    const modeLabel = executionMode === EXECUTION_MODE_LIVE ? 'Live' : 'Simulation';
    return family === 'chanakya_inverse'
        ? `Multi-slot Inverse (${modeLabel})`
        : `Eco Mode (${modeLabel})`;
};

const chanakyaRsiTone = (rsi, family) => {
    if (rsi == null) return 'default';
    if (family === 'chanakya_inverse') {
        return rsi >= 76 ? 'error' : rsi <= 24 ? 'success' : 'default';
    }
    return rsi <= 31 ? 'success' : rsi >= 65 ? 'error' : 'default';
};

const chanakyaRsiZoneHint = (family, universe = 'BANKNIFTY') => (
    family === 'chanakya_inverse'
        ? `CALL zone ≥ 76 · PUT zone ≤ 24 · Wilder RSI(14) on ${universe === 'ALL' ? 'index' : universe} 1m`
        : 'CALL zone ≤ 30.99 · PUT zone ≥ 65 · Wilder RSI(14) on BankNifty 1m'
);

const CHANAKYA_INVERSE_UNIVERSE_OPTIONS = [
    { key: 'ALL', label: 'All' },
    { key: 'BANKNIFTY', label: 'Bank Nifty' },
    { key: 'NIFTY', label: 'Nifty 50' },
];
const CHANAKYA_INVERSE_UNIVERSE_STORAGE_KEY = 'qcockpit:chanakyaInverseUniverse';

const readStoredChanakyaInverseUniverse = () => {
    if (typeof window === 'undefined') {
        return 'ALL';
    }
    const stored = window.localStorage.getItem(CHANAKYA_INVERSE_UNIVERSE_STORAGE_KEY);
    return CHANAKYA_INVERSE_UNIVERSE_OPTIONS.some((option) => option.key === stored) ? stored : 'ALL';
};

const resolveCiLayerUniverse = (layer) => (
    layer?.universe
    || layer?.summary?.universe
    || String(layer?.layerId || '').split(':')[0]
    || null
);

const filterChanakyaInverseState = (state, universe) => {
    if (!state) {
        return state;
    }
    const normalized = String(universe || 'ALL').toUpperCase();
    const matchLayer = (layer) => normalized === 'ALL' || resolveCiLayerUniverse(layer) === normalized;
    const matchEvent = (event) => {
        const eventUniverse = event?.meta?.universe;
        return normalized === 'ALL' || !eventUniverse || eventUniverse === normalized;
    };

    const layers = (state.layers || []).filter(matchLayer);
    const events = (state.events || []).filter(matchEvent);
    let summary = { ...(state.summary || {}) };

    if (normalized === 'ALL') {
        const pnl = resolveChanakyaInverseLivePnl(summary, layers);
        summary = {
            ...summary,
            universe: 'ALL',
            ...pnl,
            openSlots: layers.flatMap((layer) => layer.summary?.openSlots || []),
        };
    } else {
        const primaryLayer = [...layers].sort((a, b) => {
            const aTime = a.capturedAt ? new Date(a.capturedAt).getTime() : 0;
            const bTime = b.capturedAt ? new Date(b.capturedAt).getTime() : 0;
            return bTime - aTime;
        })[0];
        const merged = primaryLayer?.summary
            ? { ...summary, ...primaryLayer.summary, universe: normalized }
            : { ...summary, universe: normalized };
        // Keep journal-backed P&L when a stale layer summary would clobber it.
        summary = {
            ...merged,
            ...resolveChanakyaInverseLivePnl(merged, layers),
            pnlSource: summary.pnlSource ?? merged.pnlSource,
            pnlTelemetryStale: summary.pnlTelemetryStale ?? merged.pnlTelemetryStale,
        };
    }

    const accounts = (state.accounts || []).map((account) => ({
        ...account,
        strategies: (account.strategies || [])
            .map((strategy) => {
                const strategyLayers = (strategy.layers || []).filter(matchLayer);
                const strategyEvents = (strategy.events || []).filter(matchEvent);
                const primaryLayer = strategyLayers[0];
                const baseSummary = normalized === 'ALL'
                    ? { ...(strategy.summary || {}), ...summary }
                    : {
                        ...(strategy.summary || {}),
                        ...(primaryLayer?.summary || {}),
                        universe: normalized,
                    };
                const pnl = resolveChanakyaInverseLivePnl(baseSummary, strategyLayers.length ? strategyLayers : layers);
                return {
                    ...strategy,
                    layers: strategyLayers,
                    events: strategyEvents,
                    netPnl: pnl.netPnl,
                    summary: {
                        ...baseSummary,
                        ...pnl,
                        pnlSource: summary.pnlSource ?? baseSummary.pnlSource,
                        pnlTelemetryStale: summary.pnlTelemetryStale ?? baseSummary.pnlTelemetryStale,
                    },
                };
            })
            .filter((strategy) => (
                normalized === 'ALL'
                || (strategy.layers && strategy.layers.length > 0)
                || (strategy.events && strategy.events.length > 0)
            )),
    }));

    const tradeHistory = (state.tradeHistory || []).filter((trade) => {
        if (normalized === 'ALL') {
            return true;
        }
        const tradeUniverse = trade?.universe ? String(trade.universe).toUpperCase() : null;
        return !tradeUniverse || tradeUniverse === normalized;
    });

    return {
        ...state,
        summary,
        layers,
        events,
        accounts,
        tradeHistory,
        universe: normalized,
    };
};

const readStoredDensity = () => {
    if (typeof window === 'undefined') {
        return COMPACT_DENSITY;
    }
    const storedValue = window.localStorage.getItem(DENSITY_STORAGE_KEY);
    return storedValue === COMFORTABLE_DENSITY ? COMFORTABLE_DENSITY : COMPACT_DENSITY;
};

const readStoredMode = () => {
    if (typeof window === 'undefined') {
        return MODE_SIMULATION;
    }
    return window.localStorage.getItem(MODE_STORAGE_KEY) === MODE_LIVE ? MODE_LIVE : MODE_SIMULATION;
};

const readStoredFamily = () => {
    if (typeof window === 'undefined') {
        return 'cobra';
    }
    const stored = window.localStorage.getItem(FAMILY_STORAGE_KEY);
    return STRATEGY_FAMILIES.some((f) => f.key === stored) ? stored : 'cobra';
};

const HeaderMetaCell = ({ label, value, detail }) => {
    const { compact } = useDensity();

    return (
        <Box
            sx={(theme) => ({
                borderRadius: INNER_RADIUS,
                border: '1px solid',
                borderColor: alpha(theme.palette.divider, 0.9),
                backgroundColor: theme.palette.mode === 'dark'
                    ? alpha('#0f172a', 0.72)
                    : alpha('#ffffff', 0.92),
                px: compact ? 1 : 1.25,
                py: compact ? 0.95 : 1.1,
                minWidth: 0,
                minHeight: compact ? 58 : 70,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
            })}
        >
            <Typography sx={eyebrowSx}>{label}</Typography>
            <Typography
                variant="body2"
                sx={{
                    mt: 0.35,
                    fontWeight: 700,
                    fontFamily: PRIMARY_FONT,
                    fontVariantNumeric: 'tabular-nums',
                    lineHeight: 1.35,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {value}
            </Typography>
            {detail ? (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        mt: 0.4,
                        display: 'block',
                        lineHeight: 1.4,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {detail}
                </Typography>
            ) : null}
        </Box>
    );
};

const MetricTile = ({ label, value, detail, tone = 'default' }) => {
    const { compact } = useDensity();

    return (
        <Box
            sx={(theme) => ({
                ...getSurfaceStyles(theme, tone === 'default' ? 'neutral' : tone, {
                    withTopBar: false,
                    muted: true,
                }),
                px: compact ? 1.25 : 1.5,
                py: compact ? 1.1 : 1.35,
                minHeight: compact ? 88 : 104,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                borderColor: alpha(
                    accentColorFor(theme, tone === 'default' ? 'neutral' : tone),
                    theme.palette.mode === 'dark' ? 0.24 : 0.12
                ),
            })}
        >
            <MetricLabel label={label} />
            <Typography
                variant="h5"
                sx={(theme) => ({
                    ...getValueStyles(theme, tone, true),
                    mt: compact ? 0.75 : 1,
                    fontSize: { xs: compact ? '1.02rem' : '1.16rem', md: compact ? '1.16rem' : '1.32rem' },
                })}
            >
                {value}
            </Typography>
            {detail ? (
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                        mt: compact ? 0.7 : 0.95,
                        display: 'block',
                        lineHeight: 1.4,
                    }}
                >
                    {detail}
                </Typography>
            ) : null}
        </Box>
    );
};

const LiveRsiCard = ({ strategyFamily, indicators = {}, phase }) => {
    const { compact } = useDensity();
    const rsi = indicators.rsi;
    const rsiUpdatedAt = indicators.rsiUpdatedAt;
    const tone = chanakyaRsiTone(rsi, strategyFamily);
    const warming = phase === 'WARMING_UP' || rsi == null;

    return (
        <Box
            sx={(theme) => ({
                borderRadius: SURFACE_RADIUS,
                border: '1px solid',
                borderColor: alpha(accentColorFor(theme, 'strategy'), 0.35),
                background: theme.palette.mode === 'dark'
                    ? `linear-gradient(135deg, ${alpha('#0f172a', 0.92)} 0%, ${alpha(accentColorFor(theme, 'strategy'), 0.12)} 100%)`
                    : `linear-gradient(135deg, ${alpha('#ffffff', 0.98)} 0%, ${alpha(accentColorFor(theme, 'strategy'), 0.08)} 100%)`,
                px: compact ? 1.35 : 1.75,
                py: compact ? 1.2 : 1.55,
            })}
        >
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={compact ? 1 : 1.5}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
                <Box>
                    <Typography sx={eyebrowSx}>Live RSI (14)</Typography>
                    <Typography
                        variant="h3"
                        sx={(theme) => ({
                            ...getValueStyles(theme, tone),
                            fontFamily: MONO_FONT,
                            fontVariantNumeric: 'tabular-nums',
                            fontWeight: 800,
                            lineHeight: 1.1,
                            mt: 0.35,
                            fontSize: compact ? '2rem' : '2.35rem',
                        })}
                    >
                        {warming ? '—' : formatNumber(rsi, 1)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', lineHeight: 1.5 }}>
                        {warming
                            ? 'Warming up — RSI recalculates on each closed 1m BankNifty candle.'
                            : chanakyaRsiZoneHint(strategyFamily)}
                    </Typography>
                </Box>
                <Stack spacing={0.45} alignItems={{ xs: 'flex-start', sm: 'flex-end' }}>
                    <StatusBadge
                        label={warming ? 'Warming' : 'Live'}
                        tone={warming ? 'info' : 'success'}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO_FONT }}>
                        {rsiUpdatedAt
                            ? `Recalc ${formatRelativeTime(rsiUpdatedAt)}`
                            : 'Awaiting first candle'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Compare with TradingView BankNifty 1m RSI(14)
                    </Typography>
                </Stack>
            </Stack>
        </Box>
    );
};

const LegRow = ({ leg, index, strategyFamily }) => {
    const { compact, phone } = useDensity();
    const entryRsi = leg?.entryRsi ?? leg?.entry_rsi;

    return (
        <Box
            sx={(theme) => ({
                display: 'grid',
                gridTemplateColumns: phone ? '1fr' : 'minmax(0, 1fr) auto',
                gap: compact ? 1 : 1.2,
                alignItems: 'center',
                borderRadius: INNER_RADIUS,
                border: '1px solid',
                borderColor: alpha(theme.palette.divider, 0.82),
                backgroundColor: theme.palette.mode === 'dark'
                    ? alpha('#0f172a', 0.58)
                    : alpha('#ffffff', 0.88),
                px: compact ? 1.05 : 1.25,
                py: compact ? 0.9 : 1,
            })}
        >
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                    {leg.symbol || leg.strike || `Leg ${index + 1}`}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
                    {[
                        leg.side,
                        leg.optionType,
                        leg.expiry,
                        leg.slot ? `Slot ${leg.slot}` : null,
                        isChanakyaDesk(strategyFamily) && entryRsi != null ? `RSI @ entry ${formatNumber(entryRsi, 1)}` : null,
                    ].filter(Boolean).join(' · ') || 'Awaiting leg detail'}
                </Typography>
            </Box>
            <Stack direction="row" spacing={0.85} alignItems="center" justifyContent={phone ? 'flex-start' : 'flex-end'}>
                {leg.qty != null ? (
                    <Typography
                        variant="body2"
                        sx={{
                            fontFamily: MONO_FONT,
                            fontWeight: 700,
                            fontVariantNumeric: 'tabular-nums',
                        }}
                    >
                        {formatNumber(leg.qty, 0)}
                    </Typography>
                ) : null}
                {leg.status ? <StatusBadge status={leg.status} /> : null}
            </Stack>
        </Box>
    );
};

const CompactMetricCell = ({ label, value, tone = 'default', detail }) => {
    const { compact } = useDensity();

    return (
        <Box
            sx={(theme) => ({
                borderRadius: INNER_RADIUS,
                border: '1px solid',
                borderColor: alpha(theme.palette.divider, 0.82),
                backgroundColor: theme.palette.mode === 'dark'
                    ? alpha('#0f172a', 0.52)
                    : alpha('#ffffff', 0.88),
                px: compact ? 1 : 1.1,
                py: compact ? 0.85 : 0.95,
                minWidth: 0,
            })}
        >
            <Typography sx={eyebrowSx}>{label}</Typography>
            <Typography
                variant="body2"
                sx={(theme) => ({
                    ...getValueStyles(theme, tone),
                    mt: 0.45,
                    fontSize: compact ? '0.84rem' : '0.88rem',
                })}
            >
                {value}
            </Typography>
            {detail ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.4, display: 'block', lineHeight: 1.45 }}>
                    {detail}
                </Typography>
            ) : null}
        </Box>
    );
};

const PositionTimelineItem = ({ item, divider }) => {
    const { compact } = useDensity();

    return (
        <Box
            sx={(theme) => ({
                borderBottom: divider ? `1px solid ${alpha(theme.palette.divider, 0.64)}` : 'none',
                pb: divider ? (compact ? 0.7 : 0.85) : 0,
            })}
        >
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={0.8}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
            >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {item.title}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO_FONT }}>
                    {formatTimestamp(item.occurredAt)}
                </Typography>
            </Stack>
            {item.detail ? (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.35, display: 'block', lineHeight: 1.55 }}>
                    {item.detail}
                </Typography>
            ) : null}
        </Box>
    );
};

const PositionHistoryCard = ({ trade, divider, strategyFamily }) => {
    const { compact } = useDensity();
    const isOpen = String(trade?.status || '').toLowerCase() === 'open';
    const pnlValue = isOpen ? (trade?.openPnl ?? trade?.pnl) : (trade?.pnlNet ?? trade?.pnl);
    const pnlTone = pnlValue == null ? 'default' : pnlValue < 0 ? 'error' : 'success';
    const hasPnlBreakdown = !isOpen && (trade?.pnlGross != null || trade?.tcaCost != null || trade?.pnlNet != null);
    const statusTone = isOpen ? 'success' : pnlTone;
    const liveStop = trade?.currentStopTrigger ?? trade?.initialStopTrigger;
    const currentOrExitPrice = isOpen ? (trade?.currentPrice ?? trade?.entryPrice) : trade?.exitPrice;
    const quantity = trade?.quantity;
    const openPositionCommentary = strategyFamily === 'chanakya_inverse'
        ? 'Position is still live — Chanakya Inverse is managing the slot trail stop.'
        : strategyFamily === 'chanakya'
            ? 'Position is still live — Chanakya Eco Mode is managing the position.'
            : 'Position is still live while qCobra manages the trailing stop.';
    const tradeCommentary = isOpen
        ? (trade?.currentCommentary || openPositionCommentary)
        : (() => {
            switch (trade?.exitReason) {
                case 'stop_hit':       return 'This position is closed because the protective stop was hit.';
                case 'trail_sl':       return 'This position closed because the trailing stop fired (price retraced from peak).';
                case 'hard_sl':        return 'This position closed at the hard stop-loss level.';
                case 'take_profit':    return 'This position closed at the take-profit level.';
                case 'time_exit_30m':  return 'This position closed via the 30-minute time exit.';
                case 'force_exit':     return 'This position closed at end of session.';
                default:               return 'This position is closed for the session.';
            }
        })();

    return (
        <Box
            sx={(theme) => ({
                borderBottom: divider ? `1px solid ${alpha(theme.palette.divider, 0.7)}` : 'none',
                pb: divider ? (compact ? 1.1 : 1.5) : 0,
            })}
        >
            <Stack spacing={compact ? 0.95 : 1.15}>
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
                >
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                            {trade?.symbol || 'Unknown Contract'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                            {[
                                trade?.side,
                                quantity != null ? `${formatNumber(quantity, 0)} qty` : null,
                                trade?.tag ? `Slot ${trade.tag}` : null,
                                isChanakyaDesk(strategyFamily) && trade?.rsi != null
                                    ? `RSI ${formatNumber(trade.rsi, 1)}`
                                    : null,
                                trade?.entryAt ? `Entered ${formatTimestamp(trade.entryAt)}` : null,
                            ].filter(Boolean).join(' · ') || 'Awaiting entry details'}
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {trade?.side ? <StatusBadge label={trade.side} tone="primary" /> : null}
                        <StatusBadge label={isOpen ? 'Open' : 'Closed'} tone={statusTone} />
                    </Stack>
                </Stack>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: 'repeat(2, minmax(0, 1fr))',
                            lg: hasPnlBreakdown ? 'repeat(6, minmax(0, 1fr))' : 'repeat(4, minmax(0, 1fr))',
                        },
                        gap: compact ? 0.85 : 1,
                    }}
                >
                    <CompactMetricCell
                        label="Entry"
                        value={trade?.entryPrice != null ? formatCurrency(trade.entryPrice) : '—'}
                        detail={
                            isChanakyaDesk(strategyFamily) && trade?.rsi != null
                                ? `RSI ${formatNumber(trade.rsi, 1)} at entry`
                                : (trade?.adxAtEntry != null
                                    ? `ADX ${formatNumber(trade.adxAtEntry, 1)} · ATR ${formatNumber(trade.atrAtEntry, 1)}`
                                    : null)
                        }
                    />
                    <CompactMetricCell
                        label={isOpen ? 'Live Price' : 'Exit'}
                        value={currentOrExitPrice != null ? formatCurrency(currentOrExitPrice) : '—'}
                        detail={trade?.exitAt && !isOpen ? `Exited ${formatTimestamp(trade.exitAt)}` : (trade?.lastUpdatedAt ? `Updated ${formatTimestamp(trade.lastUpdatedAt)}` : null)}
                    />
                    {hasPnlBreakdown ? (
                        <>
                            <CompactMetricCell
                                label="Gross"
                                value={formatCurrency(trade.pnlGross)}
                                tone={(trade.pnlGross ?? 0) < 0 ? 'error' : (trade.pnlGross ?? 0) > 0 ? 'success' : 'default'}
                            />
                            <CompactMetricCell
                                label="TCA"
                                value={formatCurrency(trade.tcaCost != null ? -Math.abs(trade.tcaCost) : null)}
                                tone="warning"
                            />
                            <CompactMetricCell
                                label="Net"
                                value={formatCurrency(trade.pnlNet ?? pnlValue)}
                                tone={pnlTone}
                            />
                        </>
                    ) : (
                        <CompactMetricCell
                            label="P&L"
                            value={formatCurrency(pnlValue)}
                            tone={pnlTone}
                            detail={isOpen ? 'Open mark-to-market on the active position.' : 'Realized result for this position.'}
                        />
                    )}
                    <CompactMetricCell
                        label="Stop"
                        value={liveStop != null ? formatNumber(liveStop) : '—'}
                        detail={trade?.trailCount ? `${formatNumber(trade.trailCount, 0)} trail updates logged` : 'Initial protective stop'}
                    />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.65 }}>
                    {tradeCommentary}
                </Typography>

                {Array.isArray(trade?.timeline) && trade.timeline.length ? (
                    <Stack spacing={0.8}>
                        {trade.timeline.map((item, index) => (
                            <PositionTimelineItem
                                key={`${trade?.id || trade?.symbol || index}-${item.type || index}-${item.occurredAt || index}`}
                                item={item}
                                divider={index < trade.timeline.length - 1}
                            />
                        ))}
                    </Stack>
                ) : null}
            </Stack>
        </Box>
    );
};

const LiveModePlaceholder = () => {
    const theme = useTheme();
    return (
        <Box
            sx={{
                p: 4,
                borderRadius: SURFACE_RADIUS,
                background: alpha(theme.palette.primary.main, 0.04),
                border: `1px dashed ${alpha(theme.palette.primary.main, 0.25)}`,
                textAlign: 'center',
                minHeight: 300,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Typography variant="h6" sx={{ fontFamily: PRIMARY_FONT, fontWeight: 600 }}>
                Live Telemetry
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 400 }}>
                Real-time strategy execution monitoring will appear here once live feeds are connected.
            </Typography>
        </Box>
    );
};

const StrategyFamilyPlaceholder = ({ family }) => {
    const theme = useTheme();
    const labels = { chanakya: 'Chanakya', arjuna: 'Arjuna', narasimha: 'Narasimha' };
    return (
        <Box
            sx={{
                p: 4,
                borderRadius: SURFACE_RADIUS,
                background: alpha(theme.palette.info.main, 0.04),
                border: `1px dashed ${alpha(theme.palette.info.main, 0.25)}`,
                textAlign: 'center',
                minHeight: 300,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Typography variant="h6" sx={{ fontFamily: PRIMARY_FONT, fontWeight: 600 }}>
                {labels[family] || family} Simulation
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, maxWidth: 400 }}>
                Strategy family under development. Telemetry and cockpit views coming soon.
            </Typography>
        </Box>
    );
};

const QCockpitPage = () => {
    const theme = useTheme();
    const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
    const baselineMonitor = useQCockpitMonitor();
    const v2Monitor = useQCockpitV2Monitor();
    const v3Monitor = useQCockpitV3Monitor();
    const v4Monitor = useQCockpitV4Monitor();
    const chanakyaMonitor = useChanakyaMonitor();
    const chanakyaInverseMonitor = useChanakyaInverseMonitor();
    const viduraMonitor = useViduraMonitor();
    const vyasaMonitor = useVyasaMonitor({ pollIntervalMs: 1000 });
    const garudaMonitor = useGarudaMonitor({ pollIntervalMs: 1000 });
    const { data: familiesPayload } = useQCockpitFamilies();
    const [mode, setMode] = useState(readStoredMode);
    const [strategyFamily, setStrategyFamily] = useState(readStoredFamily);
    const [variant, setVariant] = useState('q_cobra_v4');
    const [density, setDensity] = useState(readStoredDensity);
    const [blotterTab, setBlotterTab] = useState('positions');
    const [chanakyaInverseUniverse, setChanakyaInverseUniverse] = useState(readStoredChanakyaInverseUniverse);
    const allFamilies = useMemo(
        () => familiesPayload?.families || STRATEGY_FAMILIES.map((family) => ({
            ...family,
            executionMode: EXECUTION_MODE_SIMULATION,
        })),
        [familiesPayload?.families]
    );
    const familiesForMode = useMemo(
        () => filterFamiliesByMode(allFamilies, mode),
        [allFamilies, mode]
    );
    const activeFamilyExecutionMode = useMemo(
        () => allFamilies.find((family) => family.key === strategyFamily)?.executionMode || EXECUTION_MODE_SIMULATION,
        [allFamilies, strategyFamily]
    );
    const showStrategyDesk = useMemo(
        () => isImplementedDeskFamily(strategyFamily)
            && familiesForMode.some((family) => family.key === strategyFamily),
        [familiesForMode, strategyFamily]
    );
    const activeMonitor =
        strategyFamily === 'chanakya'
            ? chanakyaMonitor
            : strategyFamily === 'chanakya_inverse'
                ? chanakyaInverseMonitor
            : strategyFamily === 'vidura'
                ? viduraMonitor
            : variant === 'q_cobra_v4'
                ? v4Monitor
                : variant === 'q_cobra_v3'
                    ? v3Monitor
                    : variant === 'q_cobra_v2'
                        ? v2Monitor
                        : baselineMonitor;
    const { state: monitorState, connection, freshness, heartbeatAge } = activeMonitor;
    // Vidura's /state contract carries gate/positions/pnl at the TOP LEVEL, which
    // the generic strategy monitor does not surface into summary. Fetch the raw
    // contract here so the snapshot metrics and Gate panel see live gate data.
    const viduraStateView = useViduraState(strategyFamily === 'vidura');
    const state = useMemo(() => (
        strategyFamily === 'chanakya_inverse'
            ? filterChanakyaInverseState(monitorState, chanakyaInverseUniverse)
            : monitorState
    ), [monitorState, strategyFamily, chanakyaInverseUniverse]);
    const {
        infra,
        infraLoading,
        infraIssues,
        platformStatusTone,
    } = usePlatformStateModel();

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(DENSITY_STORAGE_KEY, density);
        }
    }, [density]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(MODE_STORAGE_KEY, mode);
        }
    }, [mode]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(FAMILY_STORAGE_KEY, strategyFamily);
        }
    }, [strategyFamily]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(CHANAKYA_INVERSE_UNIVERSE_STORAGE_KEY, chanakyaInverseUniverse);
        }
    }, [chanakyaInverseUniverse]);

    useEffect(() => {
        const resolved = resolveFamilyForMode(allFamilies, mode, strategyFamily);
        if (resolved && resolved !== strategyFamily) {
            setStrategyFamily(resolved);
        }
    }, [allFamilies, mode, strategyFamily]);

    const compact = !isPhone && density === COMPACT_DENSITY;
    const sectionGap = compact ? 1.5 : 2;
    const panelPadding = compact ? 1.5 : 2;
    const feedHeight = compact ? 320 : 380;
    const densityContextValue = useMemo(
        () => ({
            compact,
            phone: isPhone,
            sectionGap,
            panelPadding,
        }),
        [compact, isPhone, sectionGap, panelPadding]
    );

    const account = useMemo(() => state.accounts?.[0] || null, [state.accounts]);
    const strategy = useMemo(() => account?.strategies?.[0] || null, [account]);
    const baseSummary = strategy?.summary || state.summary || {};
    // For vidura, fold the raw /state gate/positions/pnl back onto summary so the
    // shared snapshot builder + metric chain can read them (the generic monitor
    // drops these top-level fields).
    const summary = useMemo(() => (
        strategyFamily === 'vidura'
            ? {
                ...baseSummary,
                strategyId: 'vidura',
                status: viduraStateView.status || baseSummary.status,
                gate: viduraStateView.gate || baseSummary.gate || {},
                positions: viduraStateView.positions || baseSummary.positions || {},
                pnl: viduraStateView.pnl || baseSummary.pnl || {},
            }
            : baseSummary
    ), [strategyFamily, baseSummary, viduraStateView.status, viduraStateView.gate, viduraStateView.positions, viduraStateView.pnl]);
    const latestLayer = strategy?.layers?.[0] || state.layers?.[0] || null;
    const latestActivity = state.activity?.[0] || null;
    const events = strategy?.events || state.events || [];
    const positionHistory = state.tradeHistory?.length ? state.tradeHistory : strategy?.tradeHistory || [];
    const showHistoricalTradesPanel = useMemo(() => {
        if (!showStrategyDesk) {
            return false;
        }
        if (['dhruva', 'vyasa', 'chanakya_inverse', 'vidura'].includes(strategyFamily)) {
            return true;
        }
        return strategyFamily === 'cobra'
            && ['q_cobra', 'q_cobra_v2', 'q_cobra_v3', 'q_cobra_v4'].includes(variant);
    }, [showStrategyDesk, strategyFamily, variant]);
    const historicalStrategyId = strategyFamily === 'dhruva'
        ? 'dhruva'
        : strategyFamily === 'vyasa'
        ? 'vyasa'
        : strategyFamily === 'chanakya_inverse'
            ? 'chanakya_inverse'
            : strategyFamily === 'vidura'
                ? 'vidura'
                : variant;
    const strategyLabel =
        summary.strategyLabel ||
        (strategyFamily === 'vidura'
            ? 'Vidura'
            : isChanakyaDesk(strategyFamily)
            ? chanakyaDeskTitle(strategyFamily)
            : variant === 'q_cobra_v3'
            ? 'qCobra.V3'
            : variant === 'q_cobra_v2'
                ? 'Q Cobra V2'
                : 'Q Cobra');
    const variantLabel =
        variant === 'q_cobra_v3'
            ? 'qCobra.V3'
            : variant === 'q_cobra_v2'
                ? 'Greek-Aware V2'
                : 'Baseline';
    const legs = latestLayer?.legs || latestActivity?.legs || [];
    const chanakyaInversePnl = useMemo(
        () => (
            strategyFamily === 'chanakya_inverse'
                ? resolveChanakyaInverseLivePnl(summary, state.layers || [])
                : null
        ),
        [strategyFamily, summary, state.layers],
    );
    const orders = latestActivity?.orders || strategy?.orders || [];
    const positions = latestActivity?.positions || strategy?.positions || [];
    const stops = latestActivity?.stops || strategy?.stops || [];
    const selectorDiagnostics = summary.selector || latestActivity?.meta?.selector || null;
    const selectorSelected = selectorDiagnostics?.selected || null;
    const selectorRankedCandidates = Array.isArray(selectorDiagnostics?.rankedCandidates)
        ? selectorDiagnostics.rankedCandidates
        : [];
    const selectorRejectionRows = selectorDiagnostics?.rejectionCounts
        ? Object.entries(selectorDiagnostics.rejectionCounts).map(([reason, count]) => ({
              reason,
              count,
          }))
        : [];
    const eventFeedRows = buildEventFeedRows({
        summary,
        events,
        positions,
        latestLayer,
        latestActivity,
        layers: state.layers || [],
    });
    const flatStateMessage =
        summary.stateCommentary ||
        summary.lastMessage ||
        `${strategyLabel} is currently flat while it waits for a valid setup.`;
    const liveLegsEmptyMessage = positions.length
        ? 'No leg detail is attached to the current open position snapshot yet.'
        : `No live legs because ${strategyLabel} is flat. ${flatStateMessage}`;
    const ordersEmptyMessage = positions.length
        ? 'No recent order instructions are attached to the current activity snapshot yet.'
        : `No orders yet because ${strategyLabel} has not entered a position. ${flatStateMessage}`;
    const positionsEmptyMessage = `No open positions. ${flatStateMessage}`;
    const stopsEmptyMessage = positions.length
        ? 'No protective stop details are attached to the current activity snapshot yet.'
        : `No protective stops because there is no active position. ${flatStateMessage}`;
    const positionHistoryEmptyMessage = `No ${strategyLabel} positions have been taken yet today. ${flatStateMessage}`;
    const blotterTabs = [
        { key: 'orders', label: 'Orders', count: orders.length },
        { key: 'positions', label: 'Positions', count: positions.length },
        { key: 'stops', label: 'Stops', count: stops.length },
    ];
    const blotterRows = blotterTab === 'orders' ? orders : blotterTab === 'stops' ? stops : positions;
    const blotterEmptyMessage =
        blotterTab === 'orders'
            ? ordersEmptyMessage
            : blotterTab === 'stops'
                ? stopsEmptyMessage
                : positionsEmptyMessage;
    const blotterTitle = blotterTab === 'orders' ? 'Orders' : blotterTab === 'stops' ? 'Stops' : 'Positions';
    const blotterDescription =
        blotterTab === 'orders'
            ? 'Most recent working or acknowledged order instructions.'
            : blotterTab === 'stops'
                ? 'Protective orders and stop-state visibility for the current session.'
                : 'Open position inventory reflected in the latest activity snapshot.';
    const blotterColumns =
        blotterTab === 'orders'
            ? [
                  { key: 'symbol', label: 'Symbol' },
                  { key: 'side', label: 'Side' },
                  { key: 'qty', label: 'Qty', render: (value) => formatNumber(value, 0), align: 'right', monospace: true },
                  { key: 'price', label: 'Price', render: (value) => formatNumber(value), align: 'right', monospace: true },
                  { key: 'status', label: 'Status' },
              ]
            : blotterTab === 'stops'
                ? [
                      { key: 'symbol', label: 'Symbol' },
                      { key: 'qty', label: 'Qty', render: (value) => formatNumber(value, 0), align: 'right', monospace: true },
                      { key: 'price', label: 'Limit', render: (value) => formatNumber(value), align: 'right', monospace: true },
                      { key: 'status', label: 'Status' },
                      { key: 'time', label: 'Time', render: (value) => formatTimestamp(value), monospace: true },
                  ]
                : [
                      { key: 'symbol', label: 'Symbol' },
                      { key: 'qty', label: 'Qty', render: (value) => formatNumber(value, 0), align: 'right', monospace: true },
                      { key: 'avgPrice', label: 'Avg', render: (value) => formatNumber(value), align: 'right', monospace: true },
                      { key: 'pnl', label: 'P&L', render: (value) => formatCurrency(value), align: 'right', monospace: true },
                      { key: 'status', label: 'Status' },
                  ];

    const statusTone = toneForStatus(summary.status || latestLayer?.status);
    const connectionTone = connection === 'connected' ? 'success' : connection === 'error' ? 'error' : 'warning';
    const freshnessTone = freshness?.isStale ? 'warning' : 'success';
    const tcaNetPnl = summary.tcaNetPnl ?? summary.netPnl;
    const pnlTone = tcaNetPnl == null ? 'default' : tcaNetPnl < 0 ? 'error' : 'success';

    const variantCards = [
        {
            key: 'q_cobra',
            label: 'Baseline',
            summary: baselineMonitor.state.summary || {},
            freshness: baselineMonitor.freshness || {},
            heartbeatAge: baselineMonitor.heartbeatAge,
        },
        {
            key: 'q_cobra_v2',
            label: 'Greek-Aware V2',
            summary: v2Monitor.state.summary || {},
            freshness: v2Monitor.freshness || {},
            heartbeatAge: v2Monitor.heartbeatAge,
        },
        {
            key: 'q_cobra_v3',
            label: 'qCobra.V3',
            summary: v3Monitor.state.summary || {},
            freshness: v3Monitor.freshness || {},
            heartbeatAge: v3Monitor.heartbeatAge,
        },
        {
            key: 'q_cobra_v4',
            label: 'qCobra.V4',
            summary: v4Monitor.state.summary || {},
            freshness: v4Monitor.freshness || {},
            heartbeatAge: v4Monitor.heartbeatAge,
        },
    ];

    const sessionStart = latestActivity?.session?.start || latestLayer?.startTime || null;
    const sessionStop = latestActivity?.session?.stop || latestLayer?.stopTime || null;
    const sessionWindow = [sessionStart, sessionStop].filter(Boolean).join(' - ') || '—';

    const headerMetrics = [
        {
            label: 'Strategy Status',
            value: formatChipText(summary.status || latestLayer?.status || 'initialising'),
            tone: statusTone,
            detail: summary.regime ? `Regime ${formatChipText(summary.regime)}` : 'State awaiting full classification',
        },
        {
            label: 'Live P&L',
            value: formatCurrency(tcaNetPnl),
            tone: pnlTone,
            detail: `Gross ${formatCurrency(summary.grossPnl ?? summary.netPnl)} · TCA ${formatCurrency(summary.tcaCost)}`,
        },
        {
            label: 'Exposure',
            value: positions.length ? `${positions.length} positions` : 'Flat',
            tone: positions.length ? 'primary' : 'default',
            detail: `${orders.length} orders · ${stops.length} stops`,
        },
        {
            label: 'Heartbeat',
            value: heartbeatAge == null ? '—' : `${heartbeatAge}s`,
            tone: freshnessTone,
            detail: freshness?.lastUpdatedAt
                ? `Last update ${formatRelativeTime(freshness.lastUpdatedAt)}`
                : `Awaiting first ${strategyLabel} sample`,
        },
        {
            label: 'Platform',
            value: formatChipText(infra?.overallStatus || (infraLoading ? 'loading' : 'waiting')),
            tone: platformStatusTone,
            detail: infraIssues.length
                ? `${infraIssues.length} services need attention`
                : infraLoading
                    ? 'Collecting infra telemetry'
                    : 'md_norm and quant-platform aligned',
        },
    ];

    const strategyDetailMetrics = [
        {
            label: 'Connection',
            value: formatChipText(connection || 'waiting'),
            tone: connectionTone,
            description: 'Live SSE connection state between qCockpit and the strategy telemetry stream.',
        },
        {
            label: 'Heartbeat Age',
            value: heartbeatAge == null ? '—' : `${heartbeatAge}s`,
            tone: freshnessTone,
            description: 'Seconds since the strategy last published a telemetry heartbeat. Stale means the strategy loop may have hung or stopped.',
        },
        {
            label: 'Regime',
            value: formatChipText(summary.regime || '—'),
            tone: toneForStatus(summary.regime),
            description: 'ADX/ATR-based market regime. TRENDING = ADX > 25 (directional). RANGING = ADX < 18 (choppy). VOLATILE = ATR spike above 70th percentile.',
        },
        {
            label: 'ADX',
            value: formatNumber(summary.adxValue, 1),
            description: 'Average Directional Index (14-period on 5m bars). Measures trend strength: >25 = strong trend, <20 = no trend. V1/V2 gate entries on ADX > 25.',
        },
        {
            label: 'ATR',
            value: formatNumber(summary.atrValue, 1),
            description: 'Average True Range (14-period on spot). Measures volatility in points. Used for stop-loss distance: stop = entry - (1.5 x ATR).',
        },
        {
            label: 'Flips',
            value: formatNumber(summary.flipCount, 0),
            description: 'Number of direction reversals (stop-out then re-entry) in the current session. V1 halts after 15 flips.',
        },
        {
            label: 'P&L (net)',
            value: formatCurrency(tcaNetPnl),
            tone: pnlTone,
            detail: `Gross ${formatCurrency(summary.grossPnl ?? summary.netPnl)}`,
            description: 'Session P&L after deducting all transaction costs (brokerage, STT, exchange, GST, stamp duty).',
        },
        {
            label: 'Realized (net)',
            value: formatCurrency(summary.tcaRealizedPnl ?? summary.realizedPnl),
            tone: (summary.tcaRealizedPnl ?? summary.realizedPnl) < 0 ? 'error'
                : (summary.tcaRealizedPnl ?? summary.realizedPnl) > 0 ? 'success'
                : 'default',
            detail: `Gross ${formatCurrency(summary.realizedPnl ?? summary.dayPnl)}`,
            description: 'P&L from completed (closed) trades only. Excludes any open position mark-to-market.',
        },
        {
            label: 'Open MTM (net)',
            value: formatCurrency(summary.tcaOpenPnl ?? summary.openPnl),
            tone: (summary.tcaOpenPnl ?? summary.openPnl) < 0 ? 'error'
                : (summary.tcaOpenPnl ?? summary.openPnl) > 0 ? 'success'
                : 'default',
            detail: `Gross ${formatCurrency(summary.openPnl)}`,
            description: 'Unrealized mark-to-market P&L on the currently open position, net of estimated exit TCA.',
        },
        {
            label: 'TCA Cost',
            value: formatCurrency(summary.tcaCost),
            tone: 'default',
            description: 'Cumulative transaction costs today: brokerage (Rs 20/order) + STT (0.0625% sell) + exchange (0.053%) + GST (18%) + stamp duty (0.003%).',
        },
        {
            label: 'Latest Layer Qty',
            value: formatNumber(latestLayer?.quantity, 0),
            description: 'Total option quantity in the current or most recent position layer.',
        },
    ];

    // ---- qCobra.V4 specific: replace base metrics with V4-relevant ones ----
    if (variant === 'q_cobra_v4' && strategyFamily === 'cobra') {
        const v4Ind = summary.indicators || {};
        const v4Pos = summary.position || {};
        const v4Signal = summary.lastSignal || {};
        const isMlMode = (summary.signalMode || 'rule') === 'ml';
        const mlProb = summary.mlProbabilities || {};
        const mlCfg = summary.mlConfig || {};
        // Clear V1/V2/V3 base metrics that don't apply to V4
        strategyDetailMetrics.length = 2; // keep Connection + Heartbeat Age

        // ── ML mode panel: model-driven ──
        const mlMetrics = isMlMode ? [
            {
                label: 'Signal Mode',
                value: 'ML',
                tone: 'info',
                detail: mlCfg.ceThreshold != null && mlCfg.peThreshold != null
                    ? `CE ≥ ${mlCfg.ceThreshold} · PE ≥ ${mlCfg.peThreshold}`
                    : (mlCfg.threshold != null ? `threshold ${mlCfg.threshold}` : null),
                description: 'V4 is using the trained XGBoost classifiers (CE + PE) instead of the legacy 5-gate rule. Entry fires when the winning leg clears its side-specific probability threshold. Cooldown / margin shown in detail.',
            },
            {
                label: 'P(CE)',
                value: mlProb.pCe != null ? `${formatNumber(mlProb.pCe * 100, 1)}%` : '—',
                tone: mlProb.pCe != null && mlProb.pCe >= (mlCfg.ceThreshold ?? mlCfg.threshold ?? 0.65) ? 'success' : 'default',
                description: 'Live model probability that buying the ATM call now and holding 30 minutes is net-profitable after TCA. Updated every monitor tick (~1s).',
            },
            {
                label: 'P(PE)',
                value: mlProb.pPe != null ? `${formatNumber(mlProb.pPe * 100, 1)}%` : '—',
                tone: mlProb.pPe != null && mlProb.pPe >= (mlCfg.peThreshold ?? mlCfg.threshold ?? 0.65) ? 'error' : 'default',
                description: 'Live model probability that buying the ATM put now and holding 30 minutes is net-profitable after TCA.',
            },
            {
                label: 'Winning side',
                value: mlProb.winningSide || '—',
                tone: mlProb.wouldFire ? (mlProb.winningSide === 'CE' ? 'success' : 'error') : 'default',
                detail: mlProb.pWinner != null ? `P=${formatNumber(mlProb.pWinner * 100, 1)}%` : null,
                description: 'Whichever of CE / PE has the higher live probability. "wouldFire" tone means probability has cleared the entry threshold this tick.',
            },
            // Advisory readings — these no longer drive the entry in ML
            // mode, but the indicators are still computed every candle
            // and are useful diagnostic context.
            {
                label: 'RSI (advisory)',
                value: formatNumber(v4Ind.rsi, 1),
                tone: 'default',
                description: 'RSI(14) reading on 1m spot — kept for context. In ML mode the trained model decides entries; RSI is informational only.',
            },
            {
                label: 'BB %B (advisory)',
                value: formatNumber(v4Ind.bbPctB, 3),
                tone: 'default',
                description: 'Bollinger %B on 20-period — informational only in ML mode.',
            },
            {
                label: 'Stoch K/D (advisory)',
                value: v4Ind.stochK != null ? `${formatNumber(v4Ind.stochK, 1)} / ${formatNumber(v4Ind.stochD, 1)}` : '—',
                tone: 'default',
                description: 'Stochastic 14/3 — informational only in ML mode.',
            },
            {
                label: 'OI PCR',
                value: formatNumber(summary.oiPcr, 3),
                tone: 'default',
                description: 'Live intraday OI Put/Call ratio. The model uses this as one of ~30 features (ranked #2 in importance).',
            },
            // ── Trailing stop (only meaningful when ``trail_enabled``) ──
            {
                label: 'Trail',
                value: mlCfg.trailEnabled
                    ? `arm +${formatNumber(mlCfg.trailArmPts, 0)} / dist ${formatNumber(mlCfg.trailDistancePts, 0)}`
                    : 'OFF',
                tone: mlCfg.trailEnabled ? 'success' : 'default',
                detail: mlCfg.trailEnabled
                    ? 'Client-side trailing stop. Service watches LTP and exits when retraced from peak.'
                    : null,
                description: 'Trailing stop config. When armed, the stop locks in profit by exiting if LTP retraces by `trail_distance_pts` from the running peak. Validated OOS in PR #20 (trail_a20_d15: +45% net P&L vs time-only baseline, win-rate 50%→70%, smaller worst-month drawdown).',
            },
            ...(mlCfg.trailEnabled && mlCfg.trailState ? [
                {
                    label: 'Trail state',
                    value: mlCfg.trailState.armed ? 'ARMED' : 'unarmed',
                    tone: mlCfg.trailState.armed ? 'success' : 'default',
                    detail: `peak +${formatNumber(mlCfg.trailState.peakFavorablePts, 1)}pt`,
                    description: 'Live trailing-stop state of the open position. "ARMED" means peak favorable has crossed the arm threshold, so the stop is now active. Effective trigger price = peak − distance. If LTP drops below the trigger, the position exits with reason `trail_sl`.',
                },
                {
                    label: 'Trail trigger',
                    value: `+${formatNumber(mlCfg.trailState.currentTriggerPts, 1)}pt`,
                    tone: 'info',
                    detail: 'exits at peak − distance',
                    description: 'Current effective trigger level (relative to entry price). When LTP retraces to here, the position exits.',
                },
            ] : []),
        ] : [
            {
                label: 'RSI (14)',
                value: formatNumber(v4Ind.rsi, 1),
                tone: v4Ind.rsi != null && v4Ind.rsi < 30 ? 'success'
                    : v4Ind.rsi != null && v4Ind.rsi > 70 ? 'error' : 'default',
                description: 'Relative Strength Index (14-period, Wilder smoothing) on 1m spot candles. Entry zone: < 30 for CALL (oversold), > 70 for PUT (overbought). Tight signal at < 25 / > 75.',
            },
            {
                label: 'BB %B',
                value: formatNumber(v4Ind.bbPctB, 3),
                tone: v4Ind.bbPctB != null && v4Ind.bbPctB < 0.2 ? 'success'
                    : v4Ind.bbPctB != null && v4Ind.bbPctB > 0.8 ? 'error' : 'default',
                description: 'Bollinger Band %B (20-period, 2 std dev). Shows where price sits within the band: 0 = lower band, 1 = upper band. Entry zone: < 0.20 for CALL, > 0.80 for PUT.',
            },
            {
                label: 'Stoch %K / %D',
                value: v4Ind.stochK != null ? `${formatNumber(v4Ind.stochK, 1)} / ${formatNumber(v4Ind.stochD, 1)}` : '—',
                tone: v4Ind.stochK != null && v4Ind.stochK < 20 ? 'success'
                    : v4Ind.stochK != null && v4Ind.stochK > 80 ? 'error' : 'default',
                description: 'Stochastic oscillator (14-period K, 3-period D). V4 enters only when K crosses D at extremes: K < 20 crossing above D = bullish reversal (buy CALL), K > 80 crossing below D = bearish reversal (buy PUT).',
            },
            {
                label: 'OI PCR',
                value: formatNumber(summary.oiPcr, 3),
                tone: summary.oiPcr > 1.0 ? 'success' : summary.oiPcr < 0.8 ? 'error' : 'default',
                description: 'Open Interest Put-Call Ratio from EOD options chain. PCR > 1.0 = market is put-heavy (hedged for downside, potential for upside squeeze) — confirms CALL entry. PCR < 0.9 = call-heavy — confirms PUT entry.',
            },
        ];
        const v4DayPnl = summary.tcaNetPnl ?? summary.netPnl ?? summary.dayPnl;
        const v4Realized = summary.tcaRealizedPnl ?? summary.dayPnl;
        const v4OpenContribution = summary.tcaOpenPnl ?? summary.openPnl;
        const v4FromJournal = summary.pnlSource === 'journal';
        const v4Stale = summary.pnlTelemetryStale === true;
        const v4PnlSourceDetail = v4FromJournal
            ? `Source: MySQL journal (${summary.dayTradesClosed ?? 0} closed)${v4Stale ? ' · pre-market / no telemetry yet' : ''}`
            : 'Source: in-memory (resets on restart)';
        const v4Metrics = [
            ...mlMetrics,
            {
                label: 'Day P&L',
                value: formatCurrency(v4DayPnl),
                tone: v4DayPnl > 0 ? 'success' : v4DayPnl < 0 ? 'error' : 'default',
                detail: v4PnlSourceDetail,
                description: 'Total day P&L across all positions (closed round-trips + open MTM), net of round-trip TCA. Backend reconciles this from the MySQL journal so a service restart mid-day does not lose realized P&L.',
            },
            {
                label: 'Realized (net)',
                value: formatCurrency(v4Realized),
                tone: v4Realized > 0 ? 'success' : v4Realized < 0 ? 'error' : 'default',
                detail: `Gross ${formatCurrency(summary.grossPnl ?? summary.realizedPnl)} · TCA ${formatCurrency(summary.tcaCost)}`,
                description: 'Closed-trade-only P&L after TCA. Excludes open position MTM. Computed from journal sum of every exit recorded today, so it is restart-safe.',
            },
            {
                label: 'Open MTM (net)',
                value: formatCurrency(v4OpenContribution),
                tone: v4OpenContribution > 0 ? 'success' : v4OpenContribution < 0 ? 'error' : 'default',
                description: 'Unrealized P&L on the live open position, net of estimated exit TCA. Zero when flat. Reads from in-memory telemetry (open positions are not journaled until exit).',
            },
            {
                label: 'TCA Cost',
                value: formatCurrency(summary.tcaCost),
                description: 'Cumulative realized transaction costs from closed trades today: brokerage (Rs 20/order) + STT (0.0625% sell) + exchange (0.053%) + GST (18%) + stamp duty (0.003% buy). Projected exit-TCA on open positions is not included here.',
            },
            {
                label: 'Equity',
                value: formatCurrency(summary.equity),
                detail: summary.lifetimeRealizedNet != null
                    ? `Lifetime P&L ${formatCurrency(summary.lifetimeRealizedNet)} · ${summary.lifetimeTrades ?? 0} closed`
                    : null,
                description: 'Running account equity = configured capital base + lifetime journal-realized P&L (every V4 round-trip ever closed) + today\'s open MTM. Restart-immune: rebuilt from the MySQL journal each request, so a service restart that resets the in-memory _equity counter does not produce stale values here. Used for dynamic lot sizing: budget = 80% of current equity.',
            },
            {
                label: 'Drawdown',
                value: summary.drawdownPct != null ? `${formatNumber(summary.drawdownPct, 1)}%` : '—',
                tone: summary.drawdownPct > 10 ? 'error' : summary.drawdownPct > 5 ? 'warning' : 'default',
                description: 'Peak-to-trough drawdown as % of peak equity. At > 10%: lot sizes halved. At > 25%: trading halted until DD recovers below 15%. Note: peak equity is in-memory and resets on restart; absolute drawdown may underreport if the service restarted mid-day.',
            },
            {
                label: 'Day Trades',
                value: formatNumber(summary.dayTrades, 0),
                description: 'Completed round-trip trades today (count from the journal). Each trade is a buy-and-hold cycle exited by trail SL or 30-min timer.',
            },
            {
                label: 'Premium Deployed',
                value: formatCurrency(summary.dayPremiumDeployed),
                description: 'Total option premium outlay today (sum of entry_price x qty for all entries). This is the actual cash deployed — no margin since V4 only buys options.',
            },
            {
                label: 'Spot LTP',
                value: formatNumber(summary.spotLtp, 1),
                description: 'Last traded price of the underlying index (BankNifty/Nifty spot). RSI, Bollinger, and Stochastic are computed on this price.',
            },
            {
                label: 'Last Signal',
                value: formatChipText(v4Signal.direction || '—'),
                tone: v4Signal.direction === 'CE' ? 'success'
                    : v4Signal.direction === 'PE' ? 'error' : 'default',
                detail: v4Signal.reason || null,
                description: 'Most recent V4 signal evaluation result. Shows direction (CE = buy call, PE = buy put) and the signal reason with all indicator values at trigger.',
            },
        ];
        // If position active, show hold timer
        if (v4Pos.direction) {
            v4Metrics.push(
                {
                    label: 'Position',
                    value: `${v4Pos.direction} ${v4Pos.symbol || ''}`,
                    tone: 'info',
                    detail: `${v4Pos.lots} lots @ ${formatNumber(v4Pos.entryPrice, 2)}`,
                    description: 'Currently held option position. V4 buys ATM calls (CE) or puts (PE) and holds for exactly 30 minutes with no stop loss.',
                },
                {
                    label: 'Hold Timer',
                    value: v4Pos.minutesRemaining != null
                        ? `${formatNumber(v4Pos.minutesRemaining, 1)} min left`
                        : '—',
                    tone: v4Pos.minutesRemaining != null && v4Pos.minutesRemaining <= 5 ? 'warning' : 'default',
                    description: 'Minutes remaining until the time-based exit. V4 exits at exactly 30 minutes after entry — no early exit, no stop loss.',
                },
                {
                    label: 'Unrealized P&L',
                    value: formatCurrency(v4Pos.unrealizedPnl),
                    tone: v4Pos.unrealizedPnl > 0 ? 'success' : v4Pos.unrealizedPnl < 0 ? 'error' : 'default',
                    description: 'Mark-to-market P&L on the open position: (current LTP - entry price) x quantity. Not yet realized — final P&L determined at the 30-minute exit.',
                },
            );
        }
        strategyDetailMetrics.push(...v4Metrics);
    }

    // ---- Chanakya specific: RSI + Eco state machine metrics ----
    if (strategyFamily === 'chanakya') {
        const ckInd = summary.indicators || {};
        const ckPos = summary.position || {};
        strategyDetailMetrics.length = 2; // keep Connection + Heartbeat Age
        const ckMetrics = [
            {
                label: 'RSI (14)',
                value: formatNumber(ckInd.rsi, 1),
                tone: ckInd.rsi != null && ckInd.rsi <= 31 ? 'success'
                    : ckInd.rsi != null && ckInd.rsi >= 65 ? 'error' : 'default',
                description: 'Wilder-smoothed RSI on BankNifty 1m candles (continuous across sessions). CALL zone: <= 30.99. PUT zone: >= 65.',
            },
            {
                label: 'Phase',
                value: summary.phase || '—',
                tone: summary.phase === 'ACTIVE' ? 'success' : summary.phase === 'HALTED' ? 'error' : 'default',
                description: 'WARMING_UP: RSI not ready. ARMED: scanning. ACTIVE: position open. CLOSED: EOD. HALTED: drawdown breaker.',
            },
            {
                label: 'Eco State',
                value: summary.ecoState || '—',
                tone: summary.ecoState === 'IDLE' ? 'default' : 'warning',
                description: 'IDLE: scanning. BULL_SL/BEAR_SL: watching for scenario re-entry. BULL_W35/BEAR_W60: waiting for RSI recovery.',
            },
            {
                label: 'Day P&L',
                value: formatCurrency(summary.dayPnl),
                tone: summary.dayPnl > 0 ? 'success' : summary.dayPnl < 0 ? 'error' : 'default',
                description: 'Net P&L from closed trades today (after TCA).',
            },
            {
                label: 'Equity',
                value: formatCurrency(summary.equity),
                description: 'Running simulation equity. Initial: 3L.',
            },
            {
                label: 'Drawdown',
                value: summary.drawdownPct != null ? `${formatNumber(summary.drawdownPct, 1)}%` : '—',
                tone: summary.drawdownPct > 10 ? 'error' : summary.drawdownPct > 5 ? 'warning' : 'default',
                description: 'Peak-to-trough drawdown. Halts at 25%, resumes at 15%.',
            },
            {
                label: 'Day Trades',
                value: formatNumber(summary.dayTrades, 0),
                description: 'Completed round-trip trades today.',
            },
            {
                label: 'Spot LTP',
                value: formatNumber(summary.spotLtp, 0),
                description: 'Current BankNifty index level.',
            },
            {
                label: 'Stream Freshness',
                value: summary.streamFreshness != null ? `${formatNumber(summary.streamFreshness, 1)}s` : '—',
                tone: summary.streamFreshness > 30 ? 'error' : summary.streamFreshness > 10 ? 'warning' : 'default',
                description: 'Seconds since last tick from Redis stream. > 30s = stale data.',
            },
        ];
        if (ckPos.symbol) {
            ckMetrics.push(
                { label: 'Position', value: `${ckPos.direction || '—'} ${ckPos.symbol || ''}`, tone: ckPos.direction === 'CE' ? 'success' : 'error' },
                { label: 'Entry Price', value: formatNumber(ckPos.entryPrice, 2) },
                {
                    label: 'Entry RSI',
                    value: formatNumber(ckPos.entryRsi, 1),
                    tone: chanakyaRsiTone(ckPos.entryRsi, 'chanakya'),
                    description: 'BankNifty RSI(14) at the moment this position was opened.',
                },
                { label: 'Current LTP', value: formatNumber(ckPos.currentLtp, 2) },
                { label: 'Target', value: formatNumber(ckPos.targetPrice, 2) },
                { label: 'Stoploss', value: formatNumber(ckPos.slPrice, 2) },
                {
                    label: 'Unrealized P&L',
                    value: formatCurrency(ckPos.unrealizedPnl),
                    tone: ckPos.unrealizedPnl > 0 ? 'success' : ckPos.unrealizedPnl < 0 ? 'error' : 'default',
                },
                { label: 'Hold Time', value: ckPos.holdMinutes != null ? `${formatNumber(ckPos.holdMinutes, 1)} min` : '—' },
            );
        }
        strategyDetailMetrics.push(...ckMetrics);
    }

    // ---- Chanakya Inverse specific: RSI + multi-slot metrics ----
    if (strategyFamily === 'chanakya_inverse') {
        const ckInd = summary.indicators || {};
        const openSlots = Array.isArray(summary.openSlots) ? summary.openSlots : [];
        const activeLegs = (legs || []).filter((leg) => (leg?.status || 'active') === 'active');
        strategyDetailMetrics.length = 2;
        const ciMetrics = [
            {
                label: 'RSI (14)',
                value: formatNumber(ckInd.rsi, 1),
                tone: ckInd.rsi != null && ckInd.rsi >= 76 ? 'error'
                    : ckInd.rsi != null && ckInd.rsi <= 24 ? 'success' : 'default',
                description: `Wilder-smoothed RSI on ${summary.universe && summary.universe !== 'ALL' ? summary.universe : 'index'} 1m candles. Inverse thresholds: CALL zone >= 76, PUT zone <= 24. Escalation at 81 / 15.`,
            },
            {
                label: 'Phase',
                value: summary.phase || '—',
                tone: summary.phase === 'ACTIVE' ? 'success' : summary.phase === 'HALTED' ? 'error' : 'default',
                description: 'WARMING_UP: RSI not ready. ARMED: scanning slots. ACTIVE: one or more legs open. CLOSED: EOD. HALTED: drawdown breaker.',
            },
            {
                label: 'Open Slots',
                value: openSlots.length ? openSlots.join(', ') : 'none',
                tone: openSlots.length ? 'info' : 'default',
                description: 'Slots with an active position. Inverse allows multiple concurrent legs (CALL + PUT slots).',
            },
            {
                label: 'Active Legs',
                value: formatNumber(activeLegs.length, 0),
                tone: activeLegs.length ? 'info' : 'default',
                description: 'Currently open option legs from the latest layer snapshot.',
            },
            {
                label: 'Day P&L',
                value: formatCurrency(chanakyaInversePnl?.netPnl ?? summary.netPnl ?? summary.dayPnl),
                tone: (chanakyaInversePnl?.netPnl ?? summary.netPnl ?? summary.dayPnl) > 0 ? 'success'
                    : (chanakyaInversePnl?.netPnl ?? summary.netPnl ?? summary.dayPnl) < 0 ? 'error' : 'default',
                detail: `Realized ${formatCurrency(chanakyaInversePnl?.dayPnl ?? summary.dayPnl)} · Open ${formatCurrency(chanakyaInversePnl?.openPnl ?? summary.openPnl)}`,
                description: 'Live session P&L: realized closed trades plus open-leg mark-to-market.',
            },
            {
                label: 'Realized (net)',
                value: formatCurrency(chanakyaInversePnl?.dayPnl ?? summary.dayPnl),
                tone: (chanakyaInversePnl?.dayPnl ?? summary.dayPnl) > 0 ? 'success'
                    : (chanakyaInversePnl?.dayPnl ?? summary.dayPnl) < 0 ? 'error' : 'default',
                description: 'P&L from completed round-trips today only.',
            },
            {
                label: 'Open MTM',
                value: formatCurrency(chanakyaInversePnl?.openPnl ?? summary.openPnl),
                tone: (chanakyaInversePnl?.openPnl ?? summary.openPnl) > 0 ? 'success'
                    : (chanakyaInversePnl?.openPnl ?? summary.openPnl) < 0 ? 'error' : 'default',
                description: 'Unrealized mark-to-market across all active legs.',
            },
            {
                label: 'Equity',
                value: formatCurrency(summary.equity),
                description: 'Running simulation equity.',
            },
            {
                label: 'Drawdown',
                value: summary.drawdownPct != null ? `${formatNumber(summary.drawdownPct, 1)}%` : '—',
                tone: summary.drawdownPct > 10 ? 'error' : summary.drawdownPct > 5 ? 'warning' : 'default',
                description: 'Peak-to-trough drawdown. Halts at configured limit, resumes when recovered.',
            },
        ];
        activeLegs.forEach((leg) => {
            const legEntryRsi = leg.entryRsi ?? leg.entry_rsi;
            ciMetrics.push(
                {
                    label: `${leg.slot || leg.symbol || 'Leg'}`,
                    value: `${leg.side || '—'} ${leg.symbol || ''}`,
                    tone: leg.side === 'CE' ? 'success' : leg.side === 'PE' ? 'error' : 'default',
                    detail: [
                        `@ ${formatNumber(leg.entryPrice, 2)}`,
                        `LTP ${formatNumber(leg.currentLtp, 2)}`,
                        legEntryRsi != null ? `RSI ${formatNumber(legEntryRsi, 1)}` : null,
                    ].filter(Boolean).join(' · '),
                },
            );
        });
        strategyDetailMetrics.push(...ciMetrics);
    }

    // ---- qCobra.V3 specific additions to Control Metrics ----
    if (variant === 'q_cobra_v3') {
        const cb = summary.circuitBudget || {};
        const v3Extras = [
            {
                label: 'Signal Side',
                value: formatChipText(summary.lastSignalSide || '—'),
                tone:
                    summary.lastSignalSide === 'CE'
                        ? 'success'
                        : summary.lastSignalSide === 'PE'
                            ? 'error'
                            : 'default',
                description: 'Direction from the 4-source signal fusion engine. CE = bullish (buy call), PE = bearish (buy put). Determined by majority vote of candle, tick momentum, multi-horizon momentum, and VWAP cross.',
            },
            {
                label: 'Confidence',
                value: formatChipText(summary.lastSignalConfidence || '—'),
                tone:
                    summary.lastSignalConfidence === 'HIGH'
                        ? 'success'
                        : summary.lastSignalConfidence === 'MEDIUM'
                            ? 'warning'
                            : 'default',
                description: 'Signal confidence from source agreement. HIGH = 75%+ sources agree, MEDIUM = 50%+, LOW = below 50%. V3 sizes lots by confidence: HIGH trending = 2 lots, LOW = 1 lot.',
            },
            {
                label: 'VWAP',
                value: formatNumber(summary.vwap, 1),
                description: 'Volume-Weighted Average Price for the session. One of V3\'s 4 signal sources: price above VWAP = bullish vote, below = bearish vote (with 10-point deadband).',
            },
            {
                label: 'Stop Distance',
                value: summary.stopDistancePts == null ? '—' : `${formatNumber(summary.stopDistancePts, 1)} pt`,
                detail: 'Capped 50–120pt',
                description: 'Stop-loss distance in option premium points: 1.5 x ATR, clamped between regime-adaptive floor (50-80pt) and ceiling (120pt). Applied to option price, not spot.',
            },
            {
                label: 'Round Trips',
                value: formatNumber(summary.roundTrips, 0),
                description: 'Number of completed entry-exit cycles today. V3 allows up to 6 round trips with progressive cooldown after consecutive stops.',
            },
            {
                label: 'Lots Traded',
                value: formatNumber(summary.lotsTraded, 0),
                description: 'Cumulative lots traded across all round trips today. V3 sizes 1-4 lots based on regime (trending/ranging/volatile) x confidence (high/medium/low) matrix.',
            },
            {
                label: 'Peak LTP',
                value: formatNumber(summary.peakLtpSinceEntry, 1),
                detail: summary.currentStopTrigger != null ? `stop ${formatNumber(summary.currentStopTrigger, 2)}` : null,
                description: 'Highest option LTP since entry. Used for trailing stop activation: trail activates after +80pt profit, then trails 40pt behind the peak.',
            },
            {
                label: 'Circuit Budget',
                value: formatCurrency(cb.remainingRs),
                tone: cb.tripped ? 'error' : cb.remainingRs != null && cb.remainingRs < cb.totalBudgetRs * 0.25 ? 'warning' : 'default',
                detail: cb.lotsTraded != null ? `${cb.lotsTraded} lots × ₹${formatNumber(cb.limitPerLot, 0)}/lot` : null,
                description: 'V3 circuit breaker: daily loss limit per lot (default Rs 15,000/lot). When cumulative loss exceeds budget, V3 blocks all new entries for the rest of the day.',
            },
            {
                label: 'Bars Warmed',
                value: formatNumber(summary.barsWarmed, 0),
                detail: summary.tickBufferSize != null ? `${summary.tickBufferSize} ticks / ${summary.tickBufferSpanSeconds}s` : null,
                description: 'Number of 1-minute bars accumulated since session start. V3 requires 60 warmup bars before indicators are valid and entries are allowed.',
            },
        ];
        strategyDetailMetrics.push(...v3Extras);
    }

    // Vyasa-specific metrics — driven by useVyasaMonitor (1Hz poll of
    // /api/strategies/vyasa/state). Vyasa's payload shape doesn't match
    // V4/chanakya; we derive its own snapshot + Control Metrics arrays
    // and replace strategyDetailMetrics wholesale below when family=vyasa.
    // (cobra/chanakya's Connection + Heartbeat Age semantics don't
    // translate — Vyasa polls HTTP at 1 Hz and has its own heartbeat.)
    const vyasaState = vyasaMonitor.state || {};
    const vyasaCounters = vyasaState.counters || {};
    const vyasaPnl = vyasaState.pnl || {};
    const vyasaLastEvent = (vyasaMonitor.events && vyasaMonitor.events[0]) || null;

    // Vidura gate/positions/pnl folded onto summary above (from useViduraState).
    // Declared here so both the Control-Metrics replacement and snapshot metrics
    // can reference them.
    const viduraGate = summary.gate || {};
    const viduraPositions = summary.positions || {};
    const viduraPnl = summary.pnl || {};
    const viduraInDeployZone = viduraGate.ivRank != null && viduraGate.ivThreshold != null
        && viduraGate.ivRank > viduraGate.ivThreshold;

    const vyasaSnapshotMetrics = [
        {
            label: 'State',
            value: formatChipText(vyasaState.state || '—'),
            tone:
                vyasaState.state === 'trading' ? 'success'
                    : vyasaState.state === 'armed' ? 'success'
                    : vyasaState.state === 'halted' ? 'error'
                    : vyasaState.state === 'warming_up' ? 'info'
                    : 'default',
            description: vyasaState.stateChangedAt
                ? `since ${formatRelativeTime(vyasaState.stateChangedAt)}`
                : null,
        },
        {
            label: 'Open / Cap',
            value: `${vyasaCounters.open ?? 0} / 35`,
            tone: (vyasaCounters.open ?? 0) >= 30 ? 'warning' : 'default',
        },
        {
            label: 'Trades Today',
            value: formatNumber(vyasaCounters.trades, 0),
        },
        {
            label: 'Blocked (book / cap)',
            value: `${vyasaCounters.blockedBook ?? 0} / ${vyasaCounters.blockedCap ?? 0}`,
            description: 'Signals blocked by the 1-lot-per-key rule (book) or by the 35 max-concurrent cap.',
        },
        {
            label: 'Live Orders',
            value: vyasaState.haltReason
                ? 'HALTED'
                : (vyasaMonitor.connected ? 'PAPER (false)' : 'OFFLINE'),
            tone: vyasaState.haltReason ? 'error' : 'default',
        },
        {
            label: 'Last Heartbeat',
            value: formatTimestamp(vyasaState.lastHeartbeatAt),
        },
    ];

    const vyasaDetailMetrics = [
        {
            label: 'Live P&L (net)',
            value: formatCurrency(
                (vyasaPnl.realized || 0) +
                (vyasaMonitor.liveUnrealizedFromLegs ?? vyasaPnl.unrealized ?? 0)
            ),
            tone: ((vyasaPnl.realized || 0) + (vyasaMonitor.liveUnrealizedFromLegs ?? vyasaPnl.unrealized ?? 0)) > 0 ? 'success'
                : ((vyasaPnl.realized || 0) + (vyasaMonitor.liveUnrealizedFromLegs ?? vyasaPnl.unrealized ?? 0)) < 0 ? 'error' : 'default',
            description: 'Realized + live mark-to-market across open legs. Updates live, every second.',
        },
        {
            label: 'Realized',
            value: formatCurrency(vyasaPnl.realized),
        },
        {
            label: 'Unrealized (MTM)',
            value: formatCurrency(vyasaMonitor.liveUnrealizedFromLegs ?? vyasaPnl.unrealized),
        },
        {
            label: 'Closed Today',
            value: formatNumber(vyasaMonitor.historyTotals?.closedCount, 0),
            detail: `Win ₹${formatNumber(vyasaMonitor.historyTotals?.realizedNet, 0)} · TCA ₹${formatNumber(vyasaMonitor.historyTotals?.tcaCost, 0)}`,
        },
        {
            label: 'Suite',
            value: '4-combo',
            detail: 'NIFTY-PE-30m@.60 · NIFTY-CE-30m@.70 · NIFTY-CE-15m@.60 · BANKNIFTY-PE-30m@.70',
        },
        {
            label: 'Models Loaded',
            value: vyasaState.modelVersions ? Object.keys(vyasaState.modelVersions).length : 0,
            detail: '49 features each (Greeks + skew + microstructure + bar)',
        },
        {
            label: 'Last Event',
            value: vyasaLastEvent ? formatChipText(vyasaLastEvent.severity || 'info') : '—',
            detail: vyasaLastEvent ? (vyasaLastEvent.message || '').slice(0, 80) : null,
            tone: vyasaLastEvent?.severity === 'error' ? 'error'
                : vyasaLastEvent?.severity === 'warn' ? 'warning' : 'default',
        },
        {
            label: 'API Connection',
            value: vyasaMonitor.connected ? 'connected' : 'disconnected',
            tone: vyasaMonitor.connected ? 'success' : 'error',
        },
    ];

    // When family=vyasa, replace the V1/V2/V3-shaped strategyDetailMetrics
    // wholesale. The Connection/Heartbeat semantics are different (HTTP poll
    // vs SSE) so we don't preserve the first 2 like chanakya does.
    if (strategyFamily === 'vyasa') {
        strategyDetailMetrics.length = 0;
        strategyDetailMetrics.push(...vyasaDetailMetrics);
    }

    // Vidura (frozen paper lockbox) has no ADX/ATR/flip semantics — replace the
    // cobra-shaped Control Metrics wholesale with gate/pnl readouts from /state.
    const viduraDetailMetrics = [
        {
            label: 'Total P&L',
            value: formatCurrency(viduraPnl.total),
            tone: viduraPnl.total > 0 ? 'success' : viduraPnl.total < 0 ? 'error' : 'default',
            description: 'Cumulative paper P&L across the lockbox. Paper-only, zero conviction.',
        },
        {
            label: 'Realized',
            value: formatCurrency(viduraPnl.realized),
        },
        {
            label: 'Closed Trades',
            value: formatNumber(viduraPnl.closedTrades, 0),
        },
        {
            label: 'ATM IV (mid)',
            value: viduraGate.atmIvMid != null ? formatNumber(viduraGate.atmIvMid, 2) : '—',
            description: 'Clean order-book-mid ATM implied volatility feeding the IV-rank gate.',
        },
        {
            label: 'Next Expiry',
            value: viduraGate.nextExpiry || '—',
        },
        {
            label: 'Deploy / Skip / Missed',
            value: `${viduraPnl.deployed ?? 0} / ${viduraPnl.skipped ?? 0} / ${viduraPnl.missed ?? 0}`,
        },
        {
            label: 'Telemetry',
            value: viduraStateView.error ? 'offline' : (viduraStateView.data ? 'connected' : 'waiting'),
            tone: viduraStateView.error ? 'error' : (viduraStateView.data ? 'success' : 'warning'),
        },
        {
            label: 'As Of',
            value: formatTimestamp(viduraStateView.asOf || viduraStateView.heartbeat),
        },
    ];

    if (strategyFamily === 'vidura') {
        strategyDetailMetrics.length = 0;
        strategyDetailMetrics.push(...viduraDetailMetrics);
    }

    const viduraSnapshotMetrics = [
        {
            label: 'Status',
            value: formatChipText(summary.status || 'initialising'),
            tone: summary.status === 'active' ? 'success' : summary.status === 'halted' ? 'error' : 'default',
        },
        {
            label: 'Open condors',
            value: formatNumber(viduraPositions.openCount ?? 0, 0),
            tone: (viduraPositions.openCount ?? 0) > 0 ? 'info' : 'default',
        },
        {
            label: 'IV-rank / threshold',
            value: `${viduraGate.ivRank != null ? formatNumber(viduraGate.ivRank, 2) : '—'} / ${viduraGate.ivThreshold != null ? formatNumber(viduraGate.ivThreshold, 2) : '—'}`,
            tone: viduraInDeployZone ? 'success' : 'default',
            description: 'Clean order-book-mid IV-rank must clear the threshold to deploy.',
        },
        {
            label: 'Last decision',
            value: formatChipText(viduraGate.lastDecision || '—'),
            tone: viduraGate.lastDecision === 'DEPLOY' ? 'success'
                : viduraGate.lastDecision === 'MISSED' ? 'warning' : 'default',
            description: viduraGate.lastDecisionAt ? formatRelativeTime(viduraGate.lastDecisionAt) : null,
        },
        {
            label: 'Regime',
            value: formatChipText(viduraGate.regime || '—'),
            tone: viduraGate.regime === 'clean' ? 'success' : viduraGate.regime === 'trans' ? 'warning' : 'default',
        },
        {
            label: 'Realized P&L',
            value: formatCurrency(viduraPnl.realized),
            tone: viduraPnl.realized > 0 ? 'success' : viduraPnl.realized < 0 ? 'error' : 'default',
        },
        {
            label: 'Deploy / Skip / Missed',
            value: `${viduraPnl.deployed ?? 0} / ${viduraPnl.skipped ?? 0} / ${viduraPnl.missed ?? 0}`,
        },
    ];

    // Garuda-specific metrics — driven by useGarudaMonitor (1Hz poll of
    // /api/strategies/garuda/state). 0DTE NIFTY short-ATM-straddle with two
    // arms: "executed" (C5+A overlay, places orders in live mode) and
    // "shadow" (base strategy, always synthetic — the counterfactual).
    // Trades only on NIFTY expiry days (Tuesdays); otherwise idle_non_expiry.
    const garudaStatus = garudaMonitor.status || 'offline';
    const garudaSession = garudaMonitor.session || null;
    const garudaArms = garudaMonitor.arms || {};
    const garudaExecutedArm = garudaArms.executed || {};
    const garudaShadowArm = garudaArms.shadow || {};
    const garudaSlippage = garudaMonitor.slippage || {};
    const garudaEvents = garudaMonitor.events || [];
    const garudaLastEvent = garudaEvents[0] || null;
    const garudaExecutedPnlPts = garudaSession?.executedPnlPts ?? garudaExecutedArm.pnlPts ?? 0;
    const garudaShadowPnlPts = garudaSession?.shadowPnlPts ?? garudaShadowArm.pnlPts ?? 0;
    const garudaHaltReason = garudaSession?.haltReason
        || (garudaStatus.startsWith('halted') ? garudaStatus.slice('halted_'.length) : null);
    const garudaHalted = garudaStatus.startsWith('halted') || Boolean(garudaSession?.haltReason);
    const garudaSlippageGatePts = garudaSlippage.gatePts ?? 0.5;
    const garudaSlippageBreached = garudaSlippage.avgPts != null
        && garudaSlippage.avgPts > garudaSlippageGatePts;
    const garudaTradesToday = [
        ...(garudaExecutedArm.trades || []),
        ...(garudaShadowArm.trades || []),
    ].sort((a, b) => String(a.entryTs || '').localeCompare(String(b.entryTs || '')));
    const garudaModePhase = garudaSession
        ? `${(garudaSession.mode || 'paper').toUpperCase()} · ${garudaSession.phase || '—'}`
        : '—';
    const garudaPnlTone = (pts) => (pts > 0 ? 'success' : pts < 0 ? 'error' : 'default');
    const garudaHm = (ts) => {
        if (!ts) return '—';
        const d = new Date(ts);
        if (Number.isNaN(d.getTime())) {
            const m = String(ts).match(/(\d{2}):(\d{2})/);
            return m ? `${m[1]}:${m[2]}` : '—';
        }
        return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
    };
    const garudaStatusTone = garudaHalted || garudaStatus === 'fatal' ? 'error'
        : garudaStatus === 'trading' ? 'success'
        : garudaStatus === 'done' ? 'info'
        : garudaStatus === 'idle_non_expiry' ? 'default'
        : garudaStatus === 'offline' ? 'warning'
        : 'default';

    const garudaSnapshotMetrics = [
        {
            label: 'Status',
            value: formatChipText(garudaStatus),
            tone: garudaStatusTone,
            description: garudaMonitor.tradeDate ? `trade date ${garudaMonitor.tradeDate}` : null,
        },
        {
            label: 'Mode · Phase',
            value: garudaModePhase,
            tone: garudaSession?.mode === 'live' ? 'success' : 'default',
            description: 'Phase ladder: A = paper, B = live 1 lot, C = 2 lots, D = size.',
        },
        {
            label: 'Executed PnL (pts)',
            value: formatNumber(garudaExecutedPnlPts, 2),
            tone: garudaPnlTone(garudaExecutedPnlPts),
            description: 'Executed arm — base strategy with the C5+A day-halt overlay. Places orders in live mode.',
        },
        {
            label: 'Shadow PnL (pts)',
            value: formatNumber(garudaShadowPnlPts, 2),
            tone: garudaPnlTone(garudaShadowPnlPts),
            description: 'Shadow arm — base strategy without the overlay, always synthetic. The counterfactual.',
        },
        {
            label: 'Trades',
            value: formatNumber(garudaSession?.nTrades ?? 0, 0),
            detail: garudaSession?.nStops != null ? `${garudaSession.nStops} stops` : null,
        },
        {
            label: 'Slippage avg (pts)',
            value: garudaSlippage.avgPts != null
                ? `${formatNumber(garudaSlippage.avgPts, 2)} / ${formatNumber(garudaSlippageGatePts, 2)} gate`
                : '—',
            tone: garudaSlippageBreached ? 'warning' : 'default',
            description: `Average entry+exit slippage in premium points across ${garudaSlippage.n ?? 0} fills, vs the ${garudaSlippageGatePts} pt phase-promotion gate.`,
        },
        {
            label: 'Halt Reason',
            value: garudaHaltReason ? formatChipText(garudaHaltReason) : 'none',
            tone: garudaHaltReason ? 'error' : 'default',
        },
    ];

    const garudaDetailMetrics = [
        {
            label: 'Executed PnL (pts)',
            value: formatNumber(garudaExecutedPnlPts, 2),
            tone: garudaPnlTone(garudaExecutedPnlPts),
            description: 'Day PnL of the executed arm (C5+A overlay) in premium points.',
        },
        {
            label: 'Shadow PnL (pts)',
            value: formatNumber(garudaShadowPnlPts, 2),
            tone: garudaPnlTone(garudaShadowPnlPts),
            description: 'Day PnL of the shadow (base, always-synthetic) arm in premium points.',
        },
        {
            label: 'Overlay Edge (pts)',
            value: formatNumber(garudaExecutedPnlPts - garudaShadowPnlPts, 2),
            tone: garudaPnlTone(garudaExecutedPnlPts - garudaShadowPnlPts),
            description: 'Executed minus shadow — what the C5+A day-halt overlay added (or cost) today.',
        },
        {
            label: 'Trades / Stops',
            value: `${garudaSession?.nTrades ?? 0} / ${garudaSession?.nStops ?? 0}`,
        },
        {
            label: 'Slippage n / avg / max',
            value: `${garudaSlippage.n ?? 0} / ${garudaSlippage.avgPts != null ? formatNumber(garudaSlippage.avgPts, 2) : '—'} / ${garudaSlippage.maxPts != null ? formatNumber(garudaSlippage.maxPts, 2) : '—'}`,
            tone: garudaSlippageBreached ? 'warning' : 'default',
            detail: `gate ${formatNumber(garudaSlippageGatePts, 2)} pts`,
        },
        {
            label: 'Feed Gap Max',
            value: garudaSession?.feedGapMaxS != null ? `${formatNumber(garudaSession.feedGapMaxS, 1)}s` : '—',
            tone: (garudaSession?.feedGapMaxS ?? 0) > 5 ? 'warning' : 'default',
            description: 'Longest tick-feed gap seen this session. Sustained gaps trigger a feed halt.',
        },
        {
            label: 'Last Event',
            value: garudaLastEvent ? formatChipText(garudaLastEvent.kind || 'event') : '—',
            detail: garudaLastEvent ? formatTimestamp(garudaLastEvent.ts) : null,
        },
        {
            label: 'API Connection',
            value: garudaMonitor.connected ? 'connected' : 'disconnected',
            tone: garudaMonitor.connected ? 'success' : 'error',
        },
    ];

    // When family=garuda, replace the cobra-shaped strategyDetailMetrics
    // wholesale (same pattern as vyasa/vidura above).
    if (strategyFamily === 'garuda') {
        strategyDetailMetrics.length = 0;
        strategyDetailMetrics.push(...garudaDetailMetrics);
    }

    const strategySnapshotMetrics = strategyFamily === 'vidura' ? viduraSnapshotMetrics
        : strategyFamily === 'vyasa' ? vyasaSnapshotMetrics
        : strategyFamily === 'garuda' ? garudaSnapshotMetrics
        : strategyFamily === 'chanakya_inverse' ? [
        {
            label: 'RSI (14)',
            value: (summary.indicators || {}).rsi != null ? formatNumber((summary.indicators || {}).rsi, 1) : '—',
            tone: (summary.indicators || {}).rsi != null && (summary.indicators || {}).rsi >= 76 ? 'error'
                : (summary.indicators || {}).rsi != null && (summary.indicators || {}).rsi <= 24 ? 'success' : 'default',
        },
        {
            label: 'Phase',
            value: summary.phase || '—',
            tone: summary.phase === 'ACTIVE' ? 'success' : summary.phase === 'HALTED' ? 'error' : 'default',
        },
        {
            label: 'Open Slots',
            value: Array.isArray(summary.openSlots) && summary.openSlots.length
                ? summary.openSlots.join(', ')
                : 'none',
            tone: Array.isArray(summary.openSlots) && summary.openSlots.length ? 'info' : 'default',
        },
        {
            label: 'Active Legs',
            value: formatNumber((legs || []).filter((leg) => (leg?.status || 'active') === 'active').length, 0),
        },
        {
            label: 'Day P&L',
            value: formatCurrency(chanakyaInversePnl?.netPnl ?? summary.netPnl ?? summary.dayPnl),
            tone: (chanakyaInversePnl?.netPnl ?? summary.netPnl ?? summary.dayPnl) > 0 ? 'success'
                : (chanakyaInversePnl?.netPnl ?? summary.netPnl ?? summary.dayPnl) < 0 ? 'error' : 'default',
        },
        {
            label: 'Drawdown',
            value: summary.drawdownPct != null ? `${formatNumber(summary.drawdownPct, 1)}%` : '—',
            tone: summary.drawdownPct > 10 ? 'error' : summary.drawdownPct > 5 ? 'warning' : 'default',
        },
        {
            label: 'Captured',
            value: formatTimestamp(latestLayer?.capturedAt),
        },
    ] : strategyFamily === 'chanakya' ? [
        {
            label: 'RSI (14)',
            value: (summary.indicators || {}).rsi != null ? formatNumber((summary.indicators || {}).rsi, 1) : '—',
            tone: (summary.indicators || {}).rsi != null && (summary.indicators || {}).rsi <= 31 ? 'success'
                : (summary.indicators || {}).rsi != null && (summary.indicators || {}).rsi >= 65 ? 'error' : 'default',
        },
        {
            label: 'Phase',
            value: summary.phase || '—',
            tone: summary.phase === 'ACTIVE' ? 'success' : summary.phase === 'HALTED' ? 'error' : 'default',
        },
        {
            label: 'Eco State',
            value: summary.ecoState || 'IDLE',
            tone: summary.ecoState && summary.ecoState !== 'IDLE' ? 'warning' : 'default',
        },
        {
            label: 'BankNifty Spot',
            value: formatNumber(summary.spotLtp, 0),
        },
        {
            label: 'Filters Active',
            value: [summary.sameDayLoss ? 'Day-Loss Skip' : null, 'Prem 100-600', 'Skip Fri'].filter(Boolean).join(', ') || 'Standard',
            tone: summary.sameDayLoss ? 'warning' : 'default',
        },
        {
            label: 'Captured',
            value: formatTimestamp(latestLayer?.capturedAt),
        },
    ] : [
        {
            label: 'Layer',
            value: latestLayer?.layerId || '—',
        },
        {
            label: 'Captured',
            value: formatTimestamp(latestLayer?.capturedAt),
        },
        {
            label: 'Session Window',
            value: sessionWindow,
        },
        {
            label: 'Close Underlying',
            value: formatNumber(latestLayer?.closeUnderlying),
        },
        {
            label: 'Account',
            value: summary.accountAlias || summary.accountName || account?.accountAlias || 'prime',
        },
        {
            label: 'Strategy',
            value: strategyLabel,
        },
    ];

    const selectorMetrics = selectorDiagnostics
        ? [
              {
                  label: 'Selector',
                  value: selectorDiagnostics.enabled === false ? 'Disabled' : formatChipText(selectorDiagnostics.status || 'ready'),
                  tone:
                      selectorDiagnostics.status === 'selected'
                          ? 'success'
                          : selectorDiagnostics.status === 'skipped'
                              ? 'warning'
                              : 'default',
              },
              {
                  label: 'Universe',
                  value: formatNumber(selectorDiagnostics.universeSize, 0),
              },
              {
                  label: 'Eligible',
                  value: formatNumber(selectorDiagnostics.eligibleCount, 0),
                  tone: Number(selectorDiagnostics.eligibleCount) > 0 ? 'success' : 'warning',
              },
              {
                  label: 'Fallback',
                  value: selectorDiagnostics.fallbackUsed ? 'Used' : 'Not Used',
                  tone: selectorDiagnostics.fallbackUsed ? 'warning' : 'default',
              },
              {
                  label: 'Expiry Rank',
                  value: selectorSelected?.expiryRank == null ? '—' : formatNumber(selectorSelected.expiryRank, 0),
              },
              {
                  label: 'Strike',
                  value: selectorSelected?.strike == null ? '—' : formatNumber(selectorSelected.strike, 0),
              },
              {
                  label: 'Premium',
                  value: formatNumber(selectorSelected?.premium),
              },
              {
                  label: 'Delta',
                  value: formatNumber(selectorSelected?.delta, 2),
              },
              {
                  label: 'Gamma',
                  value: formatNumber(selectorSelected?.gamma, 4),
              },
              {
                  label: 'Theta',
                  value: formatNumber(selectorSelected?.theta, 2),
              },
              {
                  label: 'Vega',
                  value: formatNumber(selectorSelected?.vega, 2),
              },
              {
                  label: 'IV',
                  value: formatNumber(selectorSelected?.iv, 3),
              },
              {
                  label: 'Score',
                  value: formatNumber(selectorSelected?.totalScore, 3),
                  tone: selectorSelected?.totalScore != null ? 'success' : 'default',
              },
              {
                  label: 'Reason',
                  value: selectorDiagnostics.noSelectionReason ? formatChipText(selectorDiagnostics.noSelectionReason) : 'Selected',
                  tone: selectorDiagnostics.noSelectionReason ? 'warning' : 'success',
              },
          ]
        : [];

    const strategyBanner = freshness?.isStale
        ? {
              tone: 'warning',
              label: 'Strategy Feed',
              message: `Waiting for a fresh ${summary.strategyId || variant} heartbeat. Latest sample: ${formatTimestamp(freshness?.lastUpdatedAt)}.`,
          }
        : {
              tone: 'success',
              label: 'Strategy Feed',
              message: `qCockpit is receiving live ${strategyLabel} telemetry and session state updates.`,
          };

    return (
        <DensityContext.Provider value={densityContextValue}>
            <Box
                sx={{
                    minHeight: '70vh',
                    color: 'text.primary',
                    fontFamily: PRIMARY_FONT,
                    maxWidth: 1860,
                    mx: 'auto',
                    width: '100%',
                }}
            >
                <Stack spacing={compact ? 2.5 : 3}>
                    <Box
                        sx={(theme) => ({
                            ...getSurfaceStyles(theme, 'neutral', { withTopBar: false }),
                            borderRadius: HERO_RADIUS,
                            p: { xs: compact ? 1.5 : 2, md: compact ? 2 : 2.3 },
                            backgroundImage: theme.palette.mode === 'dark'
                                ? `linear-gradient(180deg, ${alpha('#111827', 0.8)} 0%, ${alpha('#020617', 0.88)} 100%)`
                                : `linear-gradient(180deg, ${alpha('#ffffff', 0.98)} 0%, ${alpha('#f8fafc', 0.96)} 100%)`,
                            borderColor: alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.42 : 0.92),
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                inset: 0,
                                pointerEvents: 'none',
                                backgroundImage: `
                                    linear-gradient(90deg, ${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.08 : 0.05)} 1px, transparent 1px),
                                    linear-gradient(${alpha(theme.palette.divider, theme.palette.mode === 'dark' ? 0.08 : 0.04)} 1px, transparent 1px)
                                `,
                                backgroundSize: compact ? '20px 20px' : '24px 24px',
                                opacity: 0.32,
                            },
                        })}
                    >
                        <Stack spacing={sectionGap} sx={{ position: 'relative', zIndex: 1 }}>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) minmax(360px, 460px)' },
                                    gap: { xs: 1.5, lg: 2.25 },
                                    alignItems: 'start',
                                }}
                            >
                                <Box sx={{ maxWidth: 780 }}>
                                    <Typography sx={eyebrowSx}>
                                        {mode === MODE_LIVE
                                            ? `Insights / Live${showStrategyDesk ? ` / ${STRATEGY_FAMILIES.find((f) => f.key === strategyFamily)?.label || 'Strategy'}` : ' Operations'}`
                                            : `Insights / Simulation / ${STRATEGY_FAMILIES.find((f) => f.key === strategyFamily)?.label || 'Strategy'}`}
                                    </Typography>
                                    <Typography
                                        variant="h4"
                                        sx={{
                                            mt: 0.65,
                                            fontWeight: 700,
                                            letterSpacing: '-0.045em',
                                            fontFamily: PRIMARY_FONT,
                                            lineHeight: 1.06,
                                            fontSize: { xs: '1.55rem', md: '1.85rem', xl: '2.15rem' },
                                        }}
                                    >
                                        qCockpit Control Center
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        color="text.secondary"
                                        sx={{ mt: 0.8, lineHeight: 1.65, maxWidth: 720 }}
                                    >
                                        {mode === MODE_LIVE
                                            ? (showStrategyDesk
                                                ? `Live operations cockpit for the ${STRATEGY_FAMILIES.find((f) => f.key === strategyFamily)?.label || strategyFamily} strategy family.`
                                                : 'Real-time monitoring for live strategy execution across accounts and platform health.')
                                            : strategyFamily === 'cobra'
                                                ? 'Operator-focused live telemetry for qCobra baseline, Greek-aware V2, qCobra.V3, and qCobra.V4 across strategy state, execution state, and platform health.'
                                                : `Simulation cockpit for the ${STRATEGY_FAMILIES.find((f) => f.key === strategyFamily)?.label || strategyFamily} strategy family.`}
                                    </Typography>
                                    {showStrategyDesk && (strategyFamily === 'cobra' || isChanakyaDesk(strategyFamily)) && (
                                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 1.2 }}>
                                            <StatusBadge status={summary.status || latestLayer?.status} />
                                            <StatusBadge
                                                label={freshness?.isStale ? 'Feed Stale' : 'Feed Live'}
                                                tone={freshness?.isStale ? 'warning' : 'success'}
                                            />
                                            <StatusBadge
                                                label={`Infra ${formatChipText(infra?.overallStatus || (infraLoading ? 'loading' : 'waiting'))}`}
                                                tone={platformStatusTone}
                                            />
                                        </Stack>
                                    )}
                                </Box>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                                        gap: compact ? 0.85 : 1,
                                        alignContent: 'start',
                                    }}
                                >
                                    <ToggleButtonGroup
                                        exclusive
                                        value={mode}
                                        onChange={(_event, nextMode) => {
                                            if (nextMode) {
                                                setMode(nextMode);
                                                setStrategyFamily((current) => resolveFamilyForMode(allFamilies, nextMode, current));
                                            }
                                        }}
                                        size="small"
                                        sx={{
                                            gridColumn: '1 / -1',
                                            justifySelf: 'stretch',
                                            '& .MuiToggleButton-root': {
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                px: compact ? 1 : 1.25,
                                            },
                                        }}
                                    >
                                        <ToggleButton value={MODE_SIMULATION}>Simulation</ToggleButton>
                                        <ToggleButton value={MODE_LIVE}>Live</ToggleButton>
                                    </ToggleButtonGroup>
                                    {familiesForMode.length > 0 && (
                                        <ToggleButtonGroup
                                            exclusive
                                            value={strategyFamily}
                                            onChange={(_event, nextFamily) => {
                                                if (nextFamily) {
                                                    setStrategyFamily(nextFamily);
                                                }
                                            }}
                                            size="small"
                                            sx={{
                                                gridColumn: '1 / -1',
                                                justifySelf: 'stretch',
                                                flexWrap: 'wrap',
                                                rowGap: 0.5,
                                                '& .MuiToggleButton-root': {
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    px: compact ? 0.75 : 0.95,
                                                    py: compact ? 0.4 : 0.5,
                                                    fontSize: compact ? '0.72rem' : '0.78rem',
                                                    lineHeight: 1.2,
                                                },
                                            }}
                                        >
                                            {familiesForMode.map((fam) => (
                                                <ToggleButton key={fam.key} value={fam.key}>{fam.label}</ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                    )}
                                    {showStrategyDesk && strategyFamily === 'cobra' && (
                                        <ToggleButtonGroup
                                            exclusive
                                            value={variant}
                                            onChange={(_event, nextVariant) => {
                                                if (nextVariant) {
                                                    setVariant(nextVariant);
                                                }
                                            }}
                                            size="small"
                                            sx={{
                                                gridColumn: '1 / -1',
                                                justifySelf: 'stretch',
                                                '& .MuiToggleButton-root': {
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    px: compact ? 1 : 1.25,
                                                },
                                            }}
                                        >
                                            <ToggleButton value="q_cobra">Baseline</ToggleButton>
                                            <ToggleButton value="q_cobra_v2">Greek-Aware V2</ToggleButton>
                                            <ToggleButton value="q_cobra_v3">qCobra.V3</ToggleButton>
                                            <ToggleButton value="q_cobra_v4">qCobra.V4</ToggleButton>
                                        </ToggleButtonGroup>
                                    )}
                                    {!isPhone ? (
                                        <ToggleButtonGroup
                                            exclusive
                                            value={density}
                                            onChange={(_event, nextDensity) => {
                                                if (nextDensity) {
                                                    setDensity(nextDensity);
                                                }
                                            }}
                                            size="small"
                                            sx={{
                                                gridColumn: '1 / -1',
                                                justifySelf: 'stretch',
                                                '& .MuiToggleButton-root': {
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    px: compact ? 1 : 1.25,
                                                },
                                            }}
                                        >
                                            <ToggleButton value={COMFORTABLE_DENSITY}>Comfortable</ToggleButton>
                                            <ToggleButton value={COMPACT_DENSITY}>Compact</ToggleButton>
                                        </ToggleButtonGroup>
                                    ) : null}
                                    {showStrategyDesk && (strategyFamily === 'cobra' || isChanakyaDesk(strategyFamily)) && (
                                        <>
                                            <HeaderMetaCell
                                                label="Strategy"
                                                value={isChanakyaDesk(strategyFamily) ? chanakyaDeskTitle(strategyFamily) : strategyLabel}
                                                detail={isChanakyaDesk(strategyFamily) ? chanakyaDeskSubtitle(strategyFamily, activeFamilyExecutionMode) : variantLabel}
                                            />
                                            <HeaderMetaCell
                                                label="Account"
                                                value={summary.accountAlias || summary.accountName || account?.accountAlias || 'prime'}
                                                detail={summary.client || 'Operator scope'}
                                            />
                                            <HeaderMetaCell
                                                label="Layer"
                                                value={latestLayer?.layerId || '—'}
                                                detail={sessionWindow}
                                            />
                                            <HeaderMetaCell
                                                label="Last Capture"
                                                value={formatTimestamp(latestLayer?.capturedAt)}
                                                detail={freshness?.lastUpdatedAt ? `Feed ${formatRelativeTime(freshness.lastUpdatedAt)}` : 'Waiting for telemetry'}
                                            />
                                        </>
                                    )}
                                </Box>
                            </Box>

                            {showStrategyDesk && strategyFamily === 'cobra' && (
                                <>
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(5, minmax(0, 1fr))' },
                                            gap: compact ? 1 : 1.15,
                                        }}
                                    >
                                        {headerMetrics.map((metric) => (
                                            <MetricTile
                                                key={metric.label}
                                                label={metric.label}
                                                value={metric.value}
                                                detail={metric.detail}
                                                tone={metric.tone}
                                            />
                                        ))}
                                    </Box>

                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
                                            gap: compact ? 0.9 : 1,
                                        }}
                                    >
                                        {variantCards.map((card) => (
                                            <HeaderMetaCell
                                                key={card.key}
                                                label={card.label}
                                                value={formatChipText(card.summary?.status || 'initialising')}
                                                detail={`P&L ${formatCurrency(card.summary?.tcaNetPnl ?? card.summary?.netPnl)} · HB ${
                                                    card.heartbeatAge == null ? '—' : `${card.heartbeatAge}s`
                                                }`}
                                            />
                                        ))}
                                    </Box>
                                </>
                            )}
                        </Stack>
                    </Box>

                    {/* Vyasa renders inside the SAME SectionFrame structure that
                        cobra/chanakya use. Its data flows through strategySnapshotMetrics
                        + strategyDetailMetrics (replaced with vyasa-shaped arrays above)
                        and through the title/description below. Cobra-only sub-panels
                        (4-Source Fusion, Greek Selector, V3 layer panels, blotter, etc.)
                        carry their own family/variant gates so they auto-skip for vyasa. */}
                    {/* Dhruva has its own desk (decision/gate, control metrics, narrated legs and
                        position history); the shared cobra-shaped desk below is skipped for it. */}
                    {showStrategyDesk && strategyFamily === 'dhruva' && (
                        <DhruvaDesk executionMode={activeFamilyExecutionMode} />
                    )}
                    {/* Kubera's desk reads its own tables + live snapshot (books per account, kill / pause). */}
                    {showStrategyDesk && strategyFamily === 'kubera' && <KuberaDesk />}
                    {showStrategyDesk && !OWN_DESK_FAMILIES.has(strategyFamily) && (
                    <>
                    <SectionFrame
                        accent="strategy"
                        eyebrow="Strategy State"
                        title={
                            strategyFamily === 'vyasa' ? 'Vyasa Strategy Desk'
                                : strategyFamily === 'garuda' ? 'Garuda Desk'
                                : strategyFamily === 'vidura' ? 'Vidura Strategy Desk'
                                : isChanakyaDesk(strategyFamily) ? `${chanakyaDeskTitle(strategyFamily)} Strategy Desk`
                                : `${strategyLabel} Strategy Desk`
                        }
                        description={
                            strategyFamily === 'vyasa'
                                ? '4-combo unified-book ML predictor on BANKNIFTY+NIFTY: 1 lot per (universe, expiry, strike, side), 35-cap, no SL/TP/trail (pure time exit). Live state, control metrics, and event flow update live, every second.'
                                : strategyFamily === 'garuda'
                                    ? '0DTE NIFTY short-straddle premium capture — paper-ladder simulation (executed vs shadow arms)'
                                : strategyFamily === 'vidura'
                                    ? 'Frozen paper lockbox — iron condors gated solely on clean order-book-mid IV-rank > threshold. Zero conviction, paper-only: the gate is the entire thesis. Snapshot, gate panel, and historical trades below.'
                                : strategyFamily === 'chanakya_inverse'
                                    ? 'Inverse RSI mean-reversion on Bank Nifty and Nifty 50 options — multi-slot CALL/PUT book per universe. Session context, live RSI, open slots, event flow, and execution blotter.'
                                : strategyFamily === 'chanakya'
                                    ? 'RSI(14) mean-reversion on BankNifty options (Eco Mode). Session context, live RSI, event flow, position monitoring, and execution blotter.'
                                    : `Execution posture, session context, live event flow, and protective state for the ${variantLabel.toLowerCase()} qCobra desk.`
                        }
                        status={
                            strategyFamily === 'vyasa' ? (
                                <Stack direction="row" spacing={0.9} flexWrap="wrap" useFlexGap>
                                    <StatusBadge
                                        label={vyasaState.state || 'idle'}
                                        tone={vyasaState.state === 'trading' || vyasaState.state === 'armed' ? 'success'
                                            : vyasaState.state === 'halted' ? 'error'
                                            : vyasaState.state === 'warming_up' ? 'info' : 'default'}
                                    />
                                    <StatusBadge
                                        label={vyasaMonitor.connected ? 'API Live' : 'API Offline'}
                                        tone={vyasaMonitor.connected ? 'success' : 'error'}
                                    />
                                    <StatusBadge
                                        label={vyasaState.haltReason ? `HALTED: ${vyasaState.haltReason}` : 'Paper Trade'}
                                        tone={vyasaState.haltReason ? 'error' : 'default'}
                                    />
                                </Stack>
                            ) : strategyFamily === 'garuda' ? (
                                <Stack direction="row" spacing={0.9} flexWrap="wrap" useFlexGap>
                                    <StatusBadge
                                        label={formatChipText(garudaStatus)}
                                        tone={garudaStatusTone}
                                    />
                                    <StatusBadge
                                        label={garudaMonitor.connected ? 'API Live' : 'API Offline'}
                                        tone={garudaMonitor.connected ? 'success' : 'error'}
                                    />
                                    <StatusBadge
                                        label={garudaSession?.mode === 'live' ? 'LIVE' : 'Paper Trade'}
                                        tone={garudaSession?.mode === 'live' ? 'success' : 'default'}
                                    />
                                    {garudaHalted ? (
                                        <StatusBadge
                                            label={`HALTED${garudaHaltReason ? `: ${garudaHaltReason}` : ''}`}
                                            tone="error"
                                        />
                                    ) : null}
                                </Stack>
                            ) : (
                                <Stack direction="row" spacing={0.9} flexWrap="wrap" useFlexGap>
                                    <StatusBadge status={summary.status || latestLayer?.status} />
                                    <StatusBadge
                                        label={freshness?.isStale ? 'Telemetry Stale' : 'Telemetry Live'}
                                        tone={freshness?.isStale ? 'warning' : 'success'}
                                    />
                                </Stack>
                            )
                        }
                    >
                        {/* strategyBanner is derived from cobra/chanakya monitor state;
                            for vyasa we'd want a vyasa-specific banner — skip until then. */}
                        {strategyFamily !== 'vyasa' && strategyFamily !== 'garuda' && <ToneBanner {...strategyBanner} />}

                        {strategyFamily === 'chanakya_inverse' ? (
                            <Box sx={{ mb: compact ? 1 : 1.5 }}>
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={chanakyaInverseUniverse}
                                    onChange={(_event, value) => {
                                        if (value) {
                                            setChanakyaInverseUniverse(value);
                                        }
                                    }}
                                    sx={{ flexWrap: 'wrap' }}
                                >
                                    {CHANAKYA_INVERSE_UNIVERSE_OPTIONS.map((option) => (
                                        <ToggleButton key={option.key} value={option.key}>
                                            {option.label}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </Box>
                        ) : null}

                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', lg: 'repeat(12, minmax(0, 1fr))' },
                                gap: compact ? 1.5 : 2,
                            }}
                        >
                            {isChanakyaDesk(strategyFamily) ? (
                                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 12' } }}>
                                    <LiveRsiCard
                                        strategyFamily={strategyFamily}
                                        indicators={summary.indicators || {}}
                                        phase={summary.phase}
                                    />
                                </Box>
                            ) : null}
                            <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 7' } }}>
                                <StrategySnapshotPanel
                                    strategyFamily={strategyFamily}
                                    executionMode={activeFamilyExecutionMode}
                                    action={
                                        strategyFamily === 'vyasa'
                                            ? <StatusBadge
                                                  label={vyasaMonitor.connected ? 'Live' : 'Offline'}
                                                  tone={vyasaMonitor.connected ? 'success' : 'error'}
                                              />
                                            : strategyFamily === 'garuda'
                                            ? <StatusBadge
                                                  label={garudaMonitor.connected ? 'Live' : 'Offline'}
                                                  tone={garudaMonitor.connected ? 'success' : 'error'}
                                              />
                                            : <StatusBadge status={connection || 'waiting'} label={`Feed ${connection || 'waiting'}`} tone={connectionTone} />
                                    }
                                    headlineMessage={
                                        strategyFamily === 'vyasa'
                                            ? (vyasaLastEvent?.message
                                                || `Vyasa ${vyasaState.state || 'initialising'} — ${vyasaCounters.open ?? 0} open / ${vyasaCounters.trades ?? 0} trades today · live P&L ${formatCurrency((vyasaPnl.realized || 0) + (vyasaMonitor.liveUnrealizedFromLegs ?? vyasaPnl.unrealized ?? 0))}`)
                                            : strategyFamily === 'garuda'
                                            ? (garudaStatus === 'idle_non_expiry'
                                                ? 'Non-expiry day — garuda trades NIFTY 0DTE (Tuesdays) only.'
                                                : `Garuda ${formatChipText(garudaStatus)} — ${garudaModePhase} · exec ${formatNumber(garudaExecutedPnlPts, 2)} pts / shadow ${formatNumber(garudaShadowPnlPts, 2)} pts · ${garudaSession?.nTrades ?? 0} trades`)
                                            : (summary.lastMessage || (strategyFamily === 'vidura'
                                                ? buildViduraStateMessage(summary)
                                                : strategyFamily === 'chanakya_inverse'
                                                ? buildChanakyaInverseStateMessage(summary, state.layers || [])
                                                : strategyFamily === 'chanakya'
                                                ? `Chanakya ${summary.phase || 'initialising'} — RSI ${(summary.indicators || {}).rsi != null ? formatNumber((summary.indicators || {}).rsi, 1) : '...'} | Eco: ${summary.ecoState || 'IDLE'} | Spot: ${summary.spotLtp ? formatNumber(summary.spotLtp, 0) : '...'}`
                                                : `Awaiting the first ${strategyLabel} event.`))
                                    }
                                    stateCommentary={
                                        strategyFamily !== 'vyasa' && strategyFamily !== 'garuda' && summary.stateCommentary
                                            && summary.stateCommentary !== summary.lastMessage
                                            ? summary.stateCommentary
                                            : null
                                    }
                                    metrics={strategySnapshotMetrics}
                                    modelScores={strategyFamily === 'vyasa' ? vyasaState.modelScores : null}
                                    modelScoresAsOf={strategyFamily === 'vyasa' ? (vyasaState.lastHeartbeatAt || vyasaMonitor.fetchedAt) : null}
                                    minHeight={{ lg: 280 }}
                                />
                            </Box>

                            <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 5' } }}>
                                <PanelFrame
                                    accent="strategy"
                                    eyebrow="Operational Readout"
                                    title="Control Metrics"
                                    description={
                                        strategyFamily === 'vyasa'
                                            ? 'Live P&L, position-book counters, model-load state, and event tone for Vyasa. Updates live, every second.'
                                            : strategyFamily === 'garuda'
                                                ? 'Executed vs shadow PnL, overlay edge, slippage vs the 0.5 pt gate, and feed health for Garuda. Updates live, every second.'
                                            : strategyFamily === 'vidura'
                                                ? 'Lockbox readout — paper P&L, ATM IV, next expiry, and decision counts. The gate is the entire thesis.'
                                            : strategyFamily === 'chanakya_inverse'
                                                ? 'Dense Chanakya Inverse state metrics — RSI zones, open slots, and active legs.'
                                            : strategyFamily === 'chanakya'
                                                ? 'Dense Chanakya state metrics for quick operator verification.'
                                                : `Dense ${strategyLabel} state metrics for quick operator verification.`
                                    }
                                    minHeight={{ lg: 280 }}
                                >
                                    <MetricMatrix
                                        metrics={strategyDetailMetrics}
                                        columns={{ xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(2, minmax(0, 1fr))' }}
                                    />
                                </PanelFrame>
                            </Box>

                            {/* All sub-panels below are wired to cobra/V4-derived
                                eventFeedRows / legs / tradeHistory / summary.* fields.
                                Gate them off when family=vyasa/vidura to prevent Cobra
                                data leaking into those views. Vyasa- and vidura-specific
                                panels render in the parallel blocks below. */}
                            {strategyFamily !== 'vyasa' && strategyFamily !== 'garuda' && strategyFamily !== 'vidura' && (<>
                            {variant === 'q_cobra_v3' ? (
                                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 12' } }}>
                                    <PanelFrame
                                        accent="strategy"
                                        eyebrow="4-Source Fusion"
                                        title="Signal Engine"
                                        description="Per-source vote breakdown from the qCobra.V3 signal fusion — 1m candle bias, 30s tick momentum, multi-horizon momentum, and VWAP cross."
                                    >
                                        {summary.signalEngine ? (
                                            (() => {
                                                const se = summary.signalEngine || {};
                                                const votes = se.votes || {};
                                                const inputs = se.inputs || {};
                                                const fmtDelta = (v) =>
                                                    v == null
                                                        ? '—'
                                                        : `${v >= 0 ? '+' : ''}${formatNumber(v, 2)}`;
                                                const voteTone = (v) =>
                                                    v === 'CE' ? 'success' : v === 'PE' ? 'error' : 'default';
                                                const sourceRows = [
                                                    {
                                                        label: '1m Candle',
                                                        value: formatChipText(votes.candle_1m || 'ABSTAIN'),
                                                        tone: voteTone(votes.candle_1m),
                                                        detail:
                                                            inputs.candle_1m_open != null && inputs.candle_1m_close != null
                                                                ? `O ${formatNumber(inputs.candle_1m_open, 1)} → C ${formatNumber(inputs.candle_1m_close, 1)}`
                                                                : null,
                                                    },
                                                    {
                                                        label: '30s Tick',
                                                        value: formatChipText(votes.tick_30s || 'ABSTAIN'),
                                                        tone: voteTone(votes.tick_30s),
                                                        detail: inputs.tick_30s_delta != null ? `Δ ${fmtDelta(inputs.tick_30s_delta)} pt` : null,
                                                    },
                                                    {
                                                        label: 'Momentum',
                                                        value: formatChipText(votes.momentum || 'ABSTAIN'),
                                                        tone: voteTone(votes.momentum),
                                                        detail: inputs.momentum_horizons
                                                            ? Object.entries(inputs.momentum_horizons)
                                                                  .map(([h, d]) => `${h}s:${fmtDelta(d)}`)
                                                                  .join(' · ')
                                                            : null,
                                                    },
                                                    {
                                                        label: 'VWAP',
                                                        value: formatChipText(votes.vwap || 'ABSTAIN'),
                                                        tone: voteTone(votes.vwap),
                                                        detail:
                                                            inputs.vwap != null && inputs.ltp != null
                                                                ? `LTP ${formatNumber(inputs.ltp, 1)} vs VWAP ${formatNumber(inputs.vwap, 1)}`
                                                                : null,
                                                    },
                                                ];
                                                const fusionRow = [
                                                    {
                                                        label: 'Fusion',
                                                        value: `${se.side || '—'} · ${se.confidence || '—'}`,
                                                        tone:
                                                            se.side === 'CE'
                                                                ? 'success'
                                                                : se.side === 'PE'
                                                                    ? 'error'
                                                                    : 'default',
                                                        detail: se.payload
                                                            ? `CE ${se.payload.ce ?? 0} / PE ${se.payload.pe ?? 0} of ${se.payload.non_abstain ?? 0}${se.payload.tie_break_used ? ' · tie-break' : ''}`
                                                            : null,
                                                    },
                                                ];
                                                return (
                                                    <Stack spacing={1.15}>
                                                        <MetricMatrix
                                                            metrics={sourceRows}
                                                            columns={{ xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }}
                                                        />
                                                        <MetricMatrix
                                                            metrics={fusionRow}
                                                            columns={{ xs: '1fr' }}
                                                        />
                                                    </Stack>
                                                );
                                            })()
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Signal engine will populate once the first 1-minute candle closes after market open.
                                            </Typography>
                                        )}
                                    </PanelFrame>
                                </Box>
                            ) : null}

                            {variant === 'q_cobra_v2' ? (
                                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 12' } }}>
                                    <PanelFrame
                                        accent="strategy"
                                        eyebrow="Greek Selector"
                                        title="Selector Diagnostics"
                                        description="Candidate filtering, Greek profile, and fallback visibility for the V2 contract chooser."
                                    >
                                        {selectorMetrics.length ? (
                                            <Stack spacing={1.15}>
                                                <MetricMatrix
                                                    metrics={selectorMetrics}
                                                    columns={{ xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))', xl: 'repeat(7, minmax(0, 1fr))' }}
                                                />
                                                {selectorRejectionRows.length ? (
                                                    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                                                        {selectorRejectionRows.map((row) => (
                                                            <StatusBadge
                                                                key={row.reason}
                                                                label={`${formatChipText(row.reason)} ${row.count}`}
                                                                tone="warning"
                                                            />
                                                        ))}
                                                    </Stack>
                                                ) : null}
                                            </Stack>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Selector diagnostics will appear here once qCobra.V2 has evaluated a contract universe.
                                            </Typography>
                                        )}
                                    </PanelFrame>
                                </Box>
                            ) : null}

                            <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 7' } }}>
                                <StrategyEventFeedPanel
                                    strategyLabel={strategyLabel}
                                    eventFeedRows={eventFeedRows}
                                    feedHeight={feedHeight}
                                />
                            </Box>

                            <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 5' } }}>
                                <Stack spacing={compact ? 1.5 : 2}>
                                    <PanelFrame
                                        accent="strategy"
                                        eyebrow="Leg Structure"
                                        title="Live Legs"
                                        description={`Current working leg composition for the latest ${strategyLabel} layer or activity snapshot.`}
                                    >
                                        {legs.length ? (
                                            <Stack spacing={0.9}>
                                                {legs.map((leg, index) => (
                                                    <LegRow
                                                        key={`${leg.symbol || leg.strike || index}-${leg.side || 'leg'}`}
                                                        leg={leg}
                                                        index={index}
                                                        strategyFamily={strategyFamily}
                                                    />
                                                ))}
                                            </Stack>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                {liveLegsEmptyMessage}
                                            </Typography>
                                        )}
                                    </PanelFrame>

                                    <PanelFrame
                                        accent="strategy"
                                        eyebrow="Position History"
                                        title="Historical Positions"
                                        description={`Each ${strategyLabel} position taken today with the important steps that brought it to its current outcome.`}
                                    >
                                        {positionHistory.length ? (
                                            <Stack spacing={1.2}>
                                                {positionHistory.map((trade, index) => (
                                                    <PositionHistoryCard
                                                        key={trade.id || `${trade.symbol || 'position'}-${trade.entryAt || index}`}
                                                        trade={trade}
                                                        strategyFamily={strategyFamily}
                                                        divider={index < positionHistory.length - 1}
                                                    />
                                                ))}
                                            </Stack>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                {positionHistoryEmptyMessage}
                                            </Typography>
                                        )}
                                    </PanelFrame>
                                </Stack>
                            </Box>

                            <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 12' } }}>
                                <PanelFrame
                                    accent="strategy"
                                    eyebrow="Execution Blotter"
                                    title={`${blotterTitle} Blotter`}
                                    description="Switch between orders, positions, and stops without paying for three separate oversized cards."
                                    action={
                                        <ToggleButtonGroup
                                            exclusive
                                            size="small"
                                            value={blotterTab}
                                            onChange={(_event, nextTab) => {
                                                if (nextTab) {
                                                    setBlotterTab(nextTab);
                                                }
                                            }}
                                            sx={{
                                                '& .MuiToggleButton-root': {
                                                    textTransform: 'none',
                                                    fontWeight: 700,
                                                    px: compact ? 0.9 : 1.1,
                                                },
                                            }}
                                        >
                                            {blotterTabs.map((tab) => (
                                                <ToggleButton key={tab.key} value={tab.key}>
                                                    {tab.label} ({tab.count})
                                                </ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                    }
                                >
                                    <DataTable
                                        title={blotterTitle}
                                        description={blotterDescription}
                                        rows={blotterRows}
                                        emptyMessage={blotterEmptyMessage}
                                        accent="strategy"
                                        columns={blotterColumns}
                                        framed={false}
                                    />
                                </PanelFrame>
                            </Box>

                            {variant === 'q_cobra_v2' ? (
                                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 12' } }}>
                                    <DataTable
                                        title="Top Selector Candidates"
                                        description="Highest-ranked contracts from the latest V2 Greek-aware selection pass."
                                        rows={selectorRankedCandidates}
                                        emptyMessage="No ranked candidate diagnostics are available yet."
                                        accent="strategy"
                                        columns={[
                                            { key: 'symbol', label: 'Symbol' },
                                            { key: 'expiryRank', label: 'Rank', render: (value) => formatNumber(value, 0), align: 'right', monospace: true },
                                            { key: 'strike', label: 'Strike', render: (value) => formatNumber(value, 0), align: 'right', monospace: true },
                                            { key: 'premium', label: 'Premium', render: (value) => formatNumber(value), align: 'right', monospace: true },
                                            { key: 'delta', label: 'Delta', render: (value) => formatNumber(value, 2), align: 'right', monospace: true },
                                            { key: 'gamma', label: 'Gamma', render: (value) => formatNumber(value, 4), align: 'right', monospace: true },
                                            { key: 'theta', label: 'Theta', render: (value) => formatNumber(value, 2), align: 'right', monospace: true },
                                            { key: 'vega', label: 'Vega', render: (value) => formatNumber(value, 2), align: 'right', monospace: true },
                                            { key: 'iv', label: 'IV', render: (value) => formatNumber(value, 3), align: 'right', monospace: true },
                                            { key: 'totalScore', label: 'Score', render: (value) => formatNumber(value, 3), align: 'right', monospace: true },
                                        ]}
                                    />
                                </Box>
                            ) : null}
                            </>)}

                            {/* Vyasa-specific Event Flow / Live Legs / Historical
                                Positions inside the same SectionFrame grid. Same
                                PanelFrame look as the cobra equivalents above; data
                                comes from useVyasaMonitor (1Hz polling). */}
                            {strategyFamily === 'vyasa' && (<>
                                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 7' } }}>
                                    <PanelFrame
                                        accent="strategy"
                                        eyebrow="Event Flow"
                                        title="Vyasa Event Feed"
                                        description="Latest entry/exit/halt/OI-baseline events from the Vyasa journal. Newest first."
                                    >
                                        {(vyasaMonitor.events || []).length ? (
                                            <Box sx={{ maxHeight: feedHeight, overflowY: 'auto', pr: 0.5 }}>
                                                <Stack divider={<Divider flexItem />} spacing={0}>
                                                    {(vyasaMonitor.events || []).slice(0, 80).map((ev, i) => (
                                                        <Stack
                                                            key={ev.id || i}
                                                            direction="row"
                                                            spacing={1.2}
                                                            alignItems="flex-start"
                                                            sx={{ py: 0.85 }}
                                                        >
                                                            <Box sx={{ minWidth: 80 }}>
                                                                <Typography variant="caption" sx={{ fontFamily: MONO_FONT, fontSize: '0.72rem' }}>
                                                                    {formatTimestamp(ev.occurredAt)}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ minWidth: 64 }}>
                                                                <StatusBadge
                                                                    label={ev.severity || 'info'}
                                                                    tone={ev.severity === 'error' ? 'error' : ev.severity === 'warn' ? 'warning' : 'default'}
                                                                />
                                                            </Box>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ flexGrow: 1, fontFamily: MONO_FONT, fontSize: '0.78rem', lineHeight: 1.45 }}
                                                            >
                                                                {ev.message}
                                                            </Typography>
                                                        </Stack>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                No Vyasa events yet today.
                                            </Typography>
                                        )}
                                    </PanelFrame>
                                </Box>

                                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 5' } }}>
                                    <Stack spacing={compact ? 1.5 : 2}>
                                        <PanelFrame
                                            accent="strategy"
                                            eyebrow="Leg Structure"
                                            title="Live Legs"
                                            description="Currently-open Vyasa positions with live MTM. Updates live, every second."
                                        >
                                            {(vyasaMonitor.legs || []).length ? (
                                                <Stack spacing={0.9}>
                                                    {(vyasaMonitor.legs || []).map((leg) => {
                                                        const mtmTone = (leg.mtmPnlNet ?? 0) > 0 ? 'success.main'
                                                            : (leg.mtmPnlNet ?? 0) < 0 ? 'error.main' : 'text.primary';
                                                        return (
                                                            <Stack
                                                                key={leg.id}
                                                                direction="row"
                                                                spacing={1}
                                                                sx={{ alignItems: 'center', fontFamily: MONO_FONT, fontSize: '0.78rem' }}
                                                            >
                                                                <StatusBadge label={leg.side} tone={leg.side === 'CE' ? 'info' : 'warning'} />
                                                                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                                    <Typography variant="caption" sx={{ display: 'block', fontFamily: MONO_FONT, fontSize: '0.74rem' }}>
                                                                        {leg.symbol}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                                        K {formatNumber(leg.strike, 0)} · qty {leg.qty} · entry {formatNumber(leg.entryPrice, 2)} → LTP {formatNumber(leg.lastLtp, 2)}
                                                                    </Typography>
                                                                </Box>
                                                                <Typography variant="body2" sx={{ color: mtmTone, fontWeight: 700 }}>
                                                                    {formatCurrency(leg.mtmPnlNet)}
                                                                </Typography>
                                                            </Stack>
                                                        );
                                                    })}
                                                </Stack>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    No open positions.
                                                </Typography>
                                            )}
                                        </PanelFrame>

                                        <PanelFrame
                                            accent="strategy"
                                            eyebrow="Position History"
                                            title="Historical Positions"
                                            description="Closed Vyasa trades for the trading day, persisted across service restarts."
                                        >
                                            {(vyasaMonitor.history || []).length ? (
                                                <Box sx={{ maxHeight: feedHeight, overflowY: 'auto', pr: 0.5 }}>
                                                    <Stack divider={<Divider flexItem />} spacing={0}>
                                                        {(vyasaMonitor.history || []).slice(0, 40).map((t) => {
                                                            const tone = (t.pnlNet ?? 0) > 0 ? 'success.main'
                                                                : (t.pnlNet ?? 0) < 0 ? 'error.main' : 'text.primary';
                                                            return (
                                                                <Stack
                                                                    key={t.id}
                                                                    direction="row"
                                                                    spacing={1}
                                                                    sx={{ alignItems: 'center', py: 0.6, fontFamily: MONO_FONT, fontSize: '0.75rem' }}
                                                                >
                                                                    <StatusBadge label={t.side} tone={t.side === 'CE' ? 'info' : 'warning'} />
                                                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                                                        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.72rem' }}>
                                                                            {t.symbol}
                                                                        </Typography>
                                                                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                                                                            {formatTimestamp(t.entryTs)} → {formatTimestamp(t.exitTs)} · {t.exitReason || 'exit'}
                                                                        </Typography>
                                                                    </Box>
                                                                    <Typography variant="body2" sx={{ color: tone, fontWeight: 700 }}>
                                                                        {formatCurrency(t.pnlNet)}
                                                                    </Typography>
                                                                </Stack>
                                                            );
                                                        })}
                                                    </Stack>
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">
                                                    No closed positions yet today.
                                                </Typography>
                                            )}
                                        </PanelFrame>
                                    </Stack>
                                </Box>
                            </>)}

                            {/* Garuda desk — 0DTE NIFTY short-ATM-straddle premium
                                capture. Executed (C5+A day-halt overlay) vs shadow
                                (base, always synthetic) arms side by side, today's
                                trades, slippage vs the 0.5 pt phase gate, and the
                                raw event feed. Data from useGarudaMonitor (1Hz). */}
                            {strategyFamily === 'garuda' && (<>
                                {garudaStatus === 'idle_non_expiry' ? (
                                    <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 12' } }}>
                                        <ToneBanner
                                            tone="info"
                                            label="Idle"
                                            message="Non-expiry day — garuda trades NIFTY 0DTE (Tuesdays) only."
                                        />
                                    </Box>
                                ) : null}

                                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 7' } }}>
                                    <PanelFrame
                                        accent="strategy"
                                        eyebrow="Session"
                                        title="Session & Arms"
                                        description="Ladder session context plus the executed (overlay) vs shadow (base counterfactual) day PnL, side by side."
                                    >
                                        <Stack spacing={1.15}>
                                            <MetricMatrix
                                                metrics={[
                                                    {
                                                        label: 'Mode',
                                                        value: garudaSession ? (garudaSession.mode || 'paper').toUpperCase() : '—',
                                                        tone: garudaSession?.mode === 'live' ? 'success' : 'default',
                                                    },
                                                    {
                                                        label: 'Phase',
                                                        value: garudaSession?.phase || '—',
                                                        description: 'A = paper, B = live 1 lot, C = 2 lots, D = size.',
                                                    },
                                                    {
                                                        label: 'State',
                                                        value: formatChipText(garudaSession?.state || garudaStatus),
                                                        tone: garudaStatusTone,
                                                    },
                                                    {
                                                        label: 'Halt',
                                                        value: garudaHaltReason ? formatChipText(garudaHaltReason) : 'none',
                                                        tone: garudaHaltReason ? 'error' : 'default',
                                                    },
                                                ]}
                                                columns={{ xs: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' }}
                                            />
                                            <Stack direction="row" spacing={0.9} flexWrap="wrap" useFlexGap alignItems="center">
                                                <StatusBadge
                                                    label={`EXECUTED ${formatNumber(garudaExecutedPnlPts, 2)} pts`}
                                                    tone="success"
                                                    size="medium"
                                                />
                                                <StatusBadge
                                                    label={`SHADOW ${formatNumber(garudaShadowPnlPts, 2)} pts`}
                                                    tone="info"
                                                    size="medium"
                                                />
                                                <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO_FONT }}>
                                                    edge {formatNumber(garudaExecutedPnlPts - garudaShadowPnlPts, 2)} pts
                                                </Typography>
                                            </Stack>
                                        </Stack>
                                    </PanelFrame>
                                </Box>

                                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 5' } }}>
                                    <PanelFrame
                                        accent="strategy"
                                        eyebrow="Execution Quality"
                                        title="Slippage"
                                        description={`Fill slippage in premium points vs the ${formatNumber(garudaSlippageGatePts, 2)} pt phase-promotion gate.`}
                                    >
                                        <MetricMatrix
                                            metrics={[
                                                {
                                                    label: 'Fills',
                                                    value: formatNumber(garudaSlippage.n ?? 0, 0),
                                                },
                                                {
                                                    label: 'Avg (pts)',
                                                    value: garudaSlippage.avgPts != null ? formatNumber(garudaSlippage.avgPts, 2) : '—',
                                                    tone: garudaSlippageBreached ? 'warning' : 'default',
                                                    detail: `gate ${formatNumber(garudaSlippageGatePts, 2)}`,
                                                },
                                                {
                                                    label: 'Max (pts)',
                                                    value: garudaSlippage.maxPts != null ? formatNumber(garudaSlippage.maxPts, 2) : '—',
                                                    tone: garudaSlippage.maxPts != null && garudaSlippage.maxPts > garudaSlippageGatePts ? 'warning' : 'default',
                                                },
                                            ]}
                                            columns={{ xs: 'repeat(3, minmax(0, 1fr))' }}
                                        />
                                        {garudaSlippageBreached ? (
                                            <Box sx={{ mt: 1.15 }}>
                                                <ToneBanner
                                                    tone="warning"
                                                    label="Gate breached"
                                                    message={`Average slippage ${formatNumber(garudaSlippage.avgPts, 2)} pts exceeds the ${formatNumber(garudaSlippageGatePts, 2)} pt gate — phase promotion blocked.`}
                                                />
                                            </Box>
                                        ) : null}
                                    </PanelFrame>
                                </Box>

                                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 12' } }}>
                                    <DataTable
                                        title="Today's Trades"
                                        description="Straddle round-trips for the trade date, both arms. Executed places orders in live mode; shadow is always synthetic."
                                        rows={garudaTradesToday}
                                        emptyMessage="No garuda trades yet today."
                                        accent="strategy"
                                        columns={[
                                            {
                                                key: 'entryTs',
                                                label: 'Time',
                                                render: (value, row) => `${garudaHm(value)}${row.exitTs ? ` → ${garudaHm(row.exitTs)}` : ''}`,
                                                monospace: true,
                                            },
                                            {
                                                key: 'arm',
                                                label: 'Arm',
                                                render: (value) => (
                                                    <StatusBadge
                                                        label={value || '—'}
                                                        tone={value === 'executed' ? 'success' : 'info'}
                                                    />
                                                ),
                                            },
                                            { key: 'strike', label: 'Strike', render: (value) => formatNumber(value, 0), align: 'right', monospace: true },
                                            { key: 'v0', label: 'V0', render: (value) => formatNumber(value, 2), align: 'right', monospace: true },
                                            { key: 'credit', label: 'Credit', render: (value) => formatNumber(value, 2), align: 'right', monospace: true },
                                            { key: 'debit', label: 'Debit', render: (value) => formatNumber(value, 2), align: 'right', monospace: true },
                                            {
                                                key: 'pnlPts',
                                                label: 'PnL (pts)',
                                                align: 'right',
                                                render: (value) => (
                                                    <Typography
                                                        component="span"
                                                        variant="body2"
                                                        sx={{
                                                            fontFamily: MONO_FONT,
                                                            fontWeight: 700,
                                                            color: (value ?? 0) > 0 ? 'success.main'
                                                                : (value ?? 0) < 0 ? 'error.main' : 'text.primary',
                                                        }}
                                                    >
                                                        {formatNumber(value, 2)}
                                                    </Typography>
                                                ),
                                            },
                                            {
                                                key: 'exitReason',
                                                label: 'Exit',
                                                render: (value) => (
                                                    <StatusBadge
                                                        label={formatChipText(value || '—')}
                                                        tone={value === 'stop' ? 'error'
                                                            : value === 'stop_flagged' || value === 'exit_flagged' ? 'warning'
                                                            : value === 'eod' ? 'info' : 'default'}
                                                    />
                                                ),
                                            },
                                            {
                                                key: 'slippagePts',
                                                label: 'Slip (pts)',
                                                align: 'right',
                                                render: (value) => (
                                                    <Typography
                                                        component="span"
                                                        variant="body2"
                                                        sx={{
                                                            fontFamily: MONO_FONT,
                                                            color: value != null && value > garudaSlippageGatePts ? 'warning.main' : 'text.primary',
                                                        }}
                                                    >
                                                        {value != null ? formatNumber(value, 2) : '—'}
                                                    </Typography>
                                                ),
                                            },
                                        ]}
                                    />
                                </Box>

                                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 12' } }}>
                                    <PanelFrame
                                        accent="strategy"
                                        eyebrow="Event Flow"
                                        title="Garuda Event Feed"
                                        description="Raw journal events from the garuda service. Newest first."
                                    >
                                        {garudaEvents.length ? (
                                            <Box sx={{ maxHeight: feedHeight, overflowY: 'auto', pr: 0.5 }}>
                                                <Stack divider={<Divider flexItem />} spacing={0}>
                                                    {garudaEvents.slice(0, 80).map((ev, i) => (
                                                        <Stack
                                                            key={ev.id || i}
                                                            direction="row"
                                                            spacing={1.2}
                                                            alignItems="flex-start"
                                                            sx={{ py: 0.85 }}
                                                        >
                                                            <Box sx={{ minWidth: 80 }}>
                                                                <Typography variant="caption" sx={{ fontFamily: MONO_FONT, fontSize: '0.72rem' }}>
                                                                    {formatTimestamp(ev.ts)}
                                                                </Typography>
                                                            </Box>
                                                            <Box sx={{ minWidth: 110 }}>
                                                                <StatusBadge
                                                                    label={ev.kind || 'event'}
                                                                    tone={/halt|fatal|error/i.test(ev.kind || '') ? 'error'
                                                                        : /stop|flag|gap/i.test(ev.kind || '') ? 'warning' : 'default'}
                                                                />
                                                            </Box>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{ flexGrow: 1, fontFamily: MONO_FONT, fontSize: '0.78rem', lineHeight: 1.45, wordBreak: 'break-word' }}
                                                            >
                                                                {ev.payload == null
                                                                    ? '—'
                                                                    : typeof ev.payload === 'string'
                                                                        ? ev.payload
                                                                        : JSON.stringify(ev.payload).slice(0, 160)}
                                                            </Typography>
                                                        </Stack>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                No garuda events yet today.
                                            </Typography>
                                        )}
                                    </PanelFrame>
                                </Box>
                            </>)}

                            {/* Vidura desk layout — mirrors the standard desks:
                                row 1: Gate panel (left, span 7) + Open Condor position
                                (right, span 5); row 2: event flow full-width at the bottom.
                                The Historical Trades archive renders below the SectionFrame.
                                Gate/position data come from useViduraState
                                (/api/strategies/vidura/state); decisions from /decisions
                                inside ViduraGatePanel. */}
                            {strategyFamily === 'vidura' && (<>
                                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 7' } }}>
                                    <ViduraGatePanel
                                        gate={viduraGate}
                                        enabled={strategyFamily === 'vidura'}
                                    />
                                </Box>

                                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 5' } }}>
                                    <PanelFrame
                                        accent="strategy"
                                        eyebrow="Position"
                                        title="Open Condor"
                                        description="The live 12Δ / 300pt iron condor held to settlement (4 legs), or flat between weekly cycles."
                                    >
                                        {(viduraPositions.open || []).length ? (
                                            <Stack spacing={1.2}>
                                                {(viduraPositions.open || []).map((condor) => (
                                                    <Box
                                                        key={condor.condorId || condor.expiry}
                                                        sx={(theme) => ({
                                                            borderRadius: INNER_RADIUS,
                                                            border: '1px solid',
                                                            borderColor: alpha(theme.palette.divider, 0.82),
                                                            p: 1.1,
                                                        })}
                                                    >
                                                        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                                {condor.expiry || 'Condor'}
                                                            </Typography>
                                                            <Typography variant="caption" sx={{ fontFamily: MONO_FONT }}>
                                                                Credit {formatNumber(condor.credit, 1)}
                                                            </Typography>
                                                        </Stack>
                                                        <Stack spacing={0.55} sx={{ mt: 0.85 }}>
                                                            {(condor.legs || []).map((leg, legIndex) => (
                                                                <Stack
                                                                    key={`${leg.role || 'leg'}-${leg.strike || legIndex}`}
                                                                    direction="row"
                                                                    justifyContent="space-between"
                                                                    alignItems="baseline"
                                                                    spacing={0.75}
                                                                >
                                                                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                                        {[leg.role, leg.side, leg.strike].filter((v) => v != null && v !== '').join(' · ')}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: MONO_FONT }}>
                                                                        @{formatNumber(leg.entryPrice, 2)}{leg.slipPts != null ? ` · slip ${formatNumber(leg.slipPts, 2)}` : ''}
                                                                    </Typography>
                                                                </Stack>
                                                            ))}
                                                        </Stack>
                                                    </Box>
                                                ))}
                                            </Stack>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Flat — no open condor. Zero conviction; awaiting the next weekly gate (clean IV-rank &gt; 0.67).
                                            </Typography>
                                        )}
                                    </PanelFrame>
                                </Box>

                                <Box sx={{ gridColumn: { xs: 'auto', lg: 'span 12' } }}>
                                    <StrategyEventFeedPanel
                                        strategyLabel="Vidura"
                                        eventFeedRows={eventFeedRows}
                                        feedHeight={feedHeight}
                                    />
                                </Box>
                            </>)}
                        </Box>
                    </SectionFrame>
                    </>
                    )}
                    {mode === MODE_SIMULATION && !showStrategyDesk && !['dhruva', 'kubera', 'cobra', 'chanakya', 'chanakya_inverse', 'vyasa', 'garuda', 'vidura'].includes(strategyFamily) && (
                        <StrategyFamilyPlaceholder family={strategyFamily} />
                    )}
                    {mode === MODE_LIVE && familiesForMode.length === 0 && (
                        <LiveModePlaceholder />
                    )}
                    {showHistoricalTradesPanel ? (
                        <TradeArchivePanel
                            strategyId={historicalStrategyId}
                            strategyLabel={strategyFamily === 'dhruva' ? 'Dhruva' : strategyFamily === 'vyasa' ? 'Vyasa' : strategyFamily === 'vidura' ? 'Vidura' : strategyLabel}
                            defaultUniverse={chanakyaInverseUniverse}
                            enabled={showHistoricalTradesPanel}
                        />
                    ) : null}
                </Stack>
            </Box>
        </DensityContext.Provider>
    );
};

export default QCockpitPage;
